/**
 * ============================================================================
 *  File        : frontend/src/lib/i18n.ts
 *  Module      : lib
 *  Purpose     : 三語 UI 字典與 locale helper，避免 React 首版引入額外 i18n runtime
 *  Author      : Phoenix Lai
 *  Created     : 2026-05-30
 *  Updated     : 2026-05-30
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import type { Locale } from '../types'

type Dictionary = Record<string, string>

const zhTW: Dictionary = {
  // --- 品項明細編集（/cases/:id/items，spec §8.2）---
  'case.items.title': '品項明細',
  'case.items.subtitle': '維護品名、概算重量、最低/希望単価與荷姿；逐品可編輯（價格版本後端自動寫入）。',
  'case.items.editTitle': '編輯品項',
  'case.items.editAction': '編輯',
  'case.items.lockedHint': '案件已結案鎖定（Closed_Locked），品項不可修改。',
  'case.items.loadError': '無法載入品項',
  'case.items.success': '品項已更新',
  'case.items.error': '更新品項失敗',
  'case.items.empty': '此案件尚無品項。',
  'case.items.reasonHint': '變更原因（寫入價格版本紀錄，可留空）。',
  'common.save': '儲存',
  'field.targetUnitPrice': '希望単価',
  'field.packageForm': '荷姿',
  'field.reason': '原因',
  'app.name': 'ECO FAITH 入札協作平台',
  'app.shortName': 'EF Platform',
  'auth.email': '帳號',
  'auth.password': '密碼',
  'auth.signIn': '登入',
  'auth.signOut': '登出',
  'auth.loading': '登入中',
  'auth.error': '帳號或密碼無法登入',
  'auth.session': '目前使用者',
  'nav.dashboard': '工作台',
  'nav.masterData': '主檔管理',
  'nav.partners': '合作夥伴',
  'nav.cases': '案件管理',
  'nav.receiving': '入荷與出貨',
  'nav.inventory': '庫存',
  'nav.sales': '入札',
  'nav.settlements': '精算',
  'nav.admin': '系統管理',
  'nav.adminUsers': '使用者管理',
  'nav.adminRoles': '權限管理',
  'nav.reports': '報表',
  'nav.audit': '稽核',
  'nav.notifications': '通知',
  'desc.dashboard': '用任務、異常與模組狀態讓使用者一進來就知道下一步。',
  'desc.masterData': '把品目、素材、倉庫、場地、台秤拆成可維護表單。',
  'desc.partners': '供應商、買方、場地、物流商各自可建立、修改與停用。',
  'desc.cases': '案件不再是單一路徑，逐案拆成品目、重量、入札、精算與結案。',
  'desc.receiving': '記錄供應商送貨、荷姿、重量、目標價與實際出貨。',
  'desc.inventory': '依倉庫與品目檢視可售、保留、已售與異常重量。',
  'desc.sales': '朝日入札預定、買方出價、落札與調整獨立管理。',
  'desc.settlements': '三段式精算、稅額、差異與 non-overwrite run 分開呈現。',
  'desc.admin': '使用者、角色、2FA 與基礎權限資料拆成獨立管理介面。',
  'desc.adminRoles': '角色與權限（role / permission）綁定，獨立於案件流程管理。',
  'desc.reports': '案件進度、重量差異、精算、入札、Freee SLA 與稽核匯出集中查詢。',
  'desc.audit': '所有金額、重量、狀態與權限變更都能查 audit chain。',
  'desc.notifications': 'outbox 發送狀態、待處理通知與重送入口。',
  'status.ready': '可操作',
  'status.partial': '部分接入',
  'status.blocked': '阻塞',
  'status.pending': '待接入',
  'dashboard.title': '營運工作台',
  'dashboard.subtitle': '所有功能從清楚的模組入口開始，不再把使用者塞進單一路徑。',
  'dashboard.nextActions': '待處理',
  'dashboard.moduleHealth': '模組狀態',
  'dashboard.open': '開啟',
  'dashboard.metricCases': '開放案件',
  'dashboard.metricShipments': '待收出貨',
  'dashboard.metricInventory': '庫存異常',
  'dashboard.metricAudit': '待稽核',
  'report.progress': '案件進度報表',
  'report.weight': '重量差異報表',
  'report.settlement': '精算報表',
  'report.auction': '入札報表',
  'report.freee': 'Freee SLA 報表',
  'report.audit': 'Audit 匯出',
  // ---- 報表中心（/reports，spec §8.2）------------------------------------
  'reports.title': '報表中心',
  'reports.subtitle': '案件進度、重量差異、精算、入札、Freee SLA 與稽核集中查詢與匯出。',
  'reports.tab.casesProgress': '案件進度',
  'reports.tab.weight': '重量差異',
  'reports.tab.settlements': '精算',
  'reports.tab.auctions': '入札',
  'reports.tab.freeeSla': 'Freee SLA',
  'reports.tab.audit': 'Audit',
  'reports.filter.from': '起始日',
  'reports.filter.to': '結束日',
  'reports.filter.status': '狀態',
  'reports.filter.statusAll': '全部狀態',
  'reports.filter.statusPlaceholder': '輸入狀態（精確比對）',
  'reports.filter.caseNo': '案件番号',
  'reports.filter.caseNoPlaceholder': '案件番号（精確比對）',
  'reports.filter.apply': '套用',
  'reports.filter.reset': '清除',
  'reports.exportCsv': '匯出 CSV',
  'reports.export.empty': '目前沒有可匯出的資料',
  'reports.loadError': '無法載入報表',
  'reports.empty': '此條件下沒有資料',
  'reports.total': '共 {total} 筆',
  'reports.prev': '上一頁',
  'reports.next': '下一頁',
  'reports.page': '第 {page} 頁',
  // 案件進度欄位
  'reports.col.caseNo': '案件番号',
  'reports.col.title': '案件名',
  'reports.col.workflowState': '業務狀態',
  'reports.col.memberOrg': '出品メンバー組織',
  'reports.col.venueOrg': '会場組織',
  'reports.col.openExceptions': '未解決異常',
  'reports.col.nextStep': '下一步',
  'reports.col.createdAt': '建立時間',
  // 重量差異欄位
  'reports.col.reviewId': 'Review ID',
  'reports.col.caseId': '案件 ID',
  'reports.col.itemName': '單品名',
  'reports.col.varianceType': '差異類型',
  'reports.col.memberWeight': 'メンバー重量（kg）',
  'reports.col.venueWeight': '会場重量（kg）',
  'reports.col.finalWeight': '確定重量（kg）',
  'reports.col.varianceKg': '差異（kg）',
  'reports.col.thresholdKg': '閾値（kg）',
  'reports.col.status': '狀態',
  // 精算欄位（user-facing,不露 cell code）
  'reports.col.p1IssuedTotal': '一回目試算總額（税込）',
  'reports.col.p1Reference50': '一回目 50% 參考前受金',
  'reports.col.p1ActualRequest': '一回目 EF 實際請求',
  'reports.col.p1Status': '一回目狀態',
  'reports.col.p2PreTaxTotal': '二回目試算總額（税抜）',
  'reports.col.p2ProfitShare': '二回目利益分配額',
  'reports.col.p2ActualRequest': '二回目 EF 實際請求',
  'reports.col.p2Status': '二回目狀態',
  'reports.col.p3Status': '三回目狀態',
  'reports.col.settlementNone': '—',
  // 入札欄位
  'reports.col.biddersCount': '入札参加者數',
  'reports.col.bidsCount': '入札件數',
  'reports.col.awardsCount': '落札確定數',
  'reports.col.awardedUnitPrice': '落札単価',
  'reports.col.awardedConfirmedAt': '落札確定時間',
  // Freee SLA 欄位
  'reports.col.taskId': 'Task ID',
  'reports.col.settlementPhase': '精算回',
  'reports.col.taskType': '書類種別',
  'reports.col.issuedAt': '発行時間',
  'reports.col.warningAt': '警告期限',
  'reports.col.dueAt': '到期期限',
  'reports.col.completedAt': '完了時間',
  // Audit 欄位
  'reports.col.auditId': 'ID',
  'reports.col.actorUser': '操作者 User',
  'reports.col.organization': '組織 ID',
  'reports.col.eventName': '事件名',
  'reports.col.resourceType': '資源型別',
  'reports.col.resourceId': '資源 ID',
  'reports.col.beforeSummary': '變更前摘要',
  'reports.col.afterSummary': '變更後摘要',
  'common.create': '新增',
  'common.update': '儲存修改',
  'common.edit': '編輯',
  'common.delete': '刪除',
  'common.cancel': '取消',
  'common.refresh': '重新整理',
  'common.search': '搜尋',
  'common.empty': '目前沒有資料',
  'common.loading': '讀取中',
  'common.error': '無法取得資料',
  'common.form': '資料表單',
  'common.table': '資料列表',
  'common.confirmDelete': '確定要刪除這筆資料？',
  'common.saved': '已儲存',
  'common.required': '必填',
  'common.underConstruction': '施工中',
  'common.underConstructionDesc': '此畫面正在建置中，路由與導覽已就緒，功能稍後上線。',
  'case.new': '新增案件',
  'rbac.forbidden': '權限不足',
  'rbac.forbiddenTitle': '無法存取此頁',
  'rbac.forbiddenDesc': '您的角色未獲授權存取此功能，請洽系統管理者。',
  'rbac.disabledTooltip': '此功能尚未開放，待業主確認後啟用。',
  'field.id': 'ID',
  'field.code': '代碼',
  'field.orgCode': '組織代碼',
  'field.orgType': '夥伴類型',
  'field.legalName': '法定名稱',
  'field.countryCode': '國別',
  'field.registrationNo': '登記號',
  'field.taxId': '稅號',
  'field.name': '名稱',
  'field.nameZh': '中文名稱',
  'field.nameJa': '日文名稱',
  'field.nameEn': '英文名稱',
  'field.type': '類型',
  'field.role': '角色',
  'field.status': '狀態',
  'field.organizationId': '組織 ID',
  'field.primaryOrganizationId': '主組織 ID',
  'field.venueId': '場地 ID',
  'field.parentId': '上層分類 ID',
  'field.categoryType': '分類類型',
  'field.sortOrder': '排序',
  'field.materialCode': '品名代碼',
  'field.venueCode': '場地代碼',
  'field.scaleCode': '台秤代碼',
  'field.scaleType': '台秤類型',
  'field.warehouseCode': '倉庫代碼',
  'field.scientificName': '學名',
  'field.defaultUom': '預設單位',
  'field.isBonded': '保稅',
  'field.email': 'Email',
  'field.password': '初始密碼',
  'field.locale': '語系',
  'field.displayName': '顯示名稱',
  'field.isSystem': '系統角色',
  'field.address': '地址',
  'field.notes': '備註',
  'field.caseNo': '案件號',
  'field.lotNo': '批次號',
  'field.sellerName': '供應商名稱',
  'field.managementCode': '管理碼',
  'field.member': '供應商',
  'field.venue': '場地',
  'field.material': '品名',
  'field.weight': '重量 kg',
  'field.actualWeight': '實際重量 kg',
  'field.targetPrice': '希望價格',
  'field.packageStyle': '荷姿',
  'field.quantity': '數量',
  'field.quantityUnit': '單位',
  'field.stockAvailable': '可售庫存',
  'field.stockReserved': '保留庫存',
  'field.stockSold': '已售',
  'field.message': '內容',
  'field.auditableType': '稽核對象',
  'field.auditableId': '對象 ID',
  'field.eventName': '事件',
  'field.createdAt': '建立時間',
  'admin.users': '使用者管理',
  'admin.roles': '角色管理',
  'master.categories': '品目分類',
  'master.materials': '品名素材',
  'master.venues': '場地',
  'master.scales': '台秤',
  'master.warehouses': '倉庫',
  'resource.master.description': '所有主檔都可以新增與修改；刪除需由後端提供 audited soft-delete 後才開啟。',
  'resource.partners.description': '合作夥伴目錄直接接正式 `/api/v1/organizations`，支援建立、修改與停用。',
  'resource.cases.description': '案件列表接目前 market cases API，案件內會拆成工作區模組。',
  'resource.receiving.description': '先把億成派車改為朝日入札預定語意，欄位含重量、品名、希望價格、實際重量與荷姿。',
  'resource.inventory.description': '庫存頁接 `inventory/stock`，讓使用者能直接看到各倉品目數量。',
  'resource.sales.description': '入札預定、出價、落札與調整分離，避免再變成單一路徑。',
  'resource.settlements.description': '一回、二回、三回與稅後金額都要可追蹤 settlement run。',
  'resource.audit.description': '稽核與通知不是附屬頁，是交付前必須能查的基礎模組。',
  'resource.notifications.description': '通知列表與 read action 接 outbox publisher 結果。',
  'resource.reports.description': '正式報表會從 snapshot 與 audit/outbox 狀態產生，含權限隔離與匯出稽核。',
  'resource.adminUsers.description': '建立與修改登入使用者；密碼不回傳，權限與狀態變更會寫 audit。',
  'resource.adminRoles.description': '角色主檔可獨立管理，後續權限綁定不混入案件流程。',
  'option.buyer': '買方',
  'option.supplier': '供應商',
  'option.venue': '場地',
  'option.logistics': '物流',
  'option.broker': '仲介',
  'option.smelter': '冶煉商',
  'option.auditor': '稽核員',
  'option.other': '其他',
  'option.active': '啟用',
  'option.inactive': '停用',
  'option.kg': '公斤',
  'option.bagUnit': '袋',
  'option.piece': '件',
  'option.jumboBag': '太空袋',
  'option.smallFlexible': '小フレコン 2kg',
  'option.flexible': 'フレコン 3kg',
  'option.bara': 'バラ',
  'option.pallet': '棧板',
  'case.workspace': '案件工作區',
  'case.modules': '案件模組',
  'case.stage.overview': '總覽',
  'case.stage.overviewDesc': '查看案件狀態、時間線、負責人與目前風險。',
  'case.stage.items': '品項明細',
  'case.stage.itemsDesc': '維護品名、重量、荷姿、希望單價、照片與價格版本。',
  'case.stage.shipment': '出貨資料',
  'case.stage.shipmentDesc': '記錄車號、司機、出貨時間、出貨照片與磅單。',
  'case.stage.receiving': '荷受け / 重量',
  'case.stage.receivingDesc': '朝日到場重量、品名確認、商品狀態與差異。',
  'case.stage.weightAudit': '重量稽核',
  'case.stage.weightAuditDesc': '20kg 異常、報連相、雙方確認與解決方案。',
  'case.stage.p1': '一回目精算',
  'case.stage.p1Desc': 'P1 Decimal 試算、N34 手入力、audit 與 outbox。',
  'case.stage.auction': '入札管理',
  'case.stage.auctionDesc': '下見、入札者、出價、ranking、落札 snapshot。',
  'case.stage.adjustments': '落札調整',
  'case.stage.adjustmentsDesc': '希望調整、返品、預り三選一與決策稽核。',
  'case.stage.shares': '三方分配',
  'case.stage.sharesDesc': 'EF 20%、メンバー 40%、会場 40%，且 P+Q+R=K。',
  'case.stage.p2': '二回目精算',
  'case.stage.p2Desc': 'P2 試算、T38 手入力、文件發行與付款確認。',
  'case.stage.finalWeight': '最終重量',
  'case.stage.finalWeightDesc': '朝日出貨、落札業者入荷與 final weight 差異。',
  'case.stage.p3': '三回目精算',
  'case.stage.p3Desc': '預設 disabled until sign-off，避免未確認公式被誤用。',
  'case.stage.finalReport': '最終報告 / Close',
  'case.stage.finalReportDesc': '產生 snapshot、三方確認，最後進入 Closed_Locked。',
  'case.action.workflow': '狀態查詢',
  'case.action.listing': '品項資料',
  'case.action.shipment': '出貨作業',
  'case.action.receiving': '重量記錄',
  'case.action.weightAudit': '重量稽核',
  'case.action.p1': 'P1 計算',
  'case.action.auction': '入札預定',
  'case.action.adjustments': '調整決策',
  'case.action.shares': '三方分配',
  'case.action.p2': 'P2 查詢',
  'case.action.finalWeight': '最終重量',
  'case.action.p3': 'P3 邊界',
  'case.action.finalReport': '最終報告',
  // --- S2 案件列表 / 新增 / 總覽 ---
  'case.list.title': '案件列表',
  'case.list.subtitle': '查看與搜尋所有入札協作案件，點列進入案件工作區。',
  'case.list.itemCount': '品項數',
  'case.list.createdAt': '建立時間',
  'case.list.searchSeller': '搜尋供應商',
  'case.list.searchCaseNo': '搜尋案件號',
  'case.list.page': '第 {page} 頁',
  'case.list.total': '共 {total} 筆',
  'case.list.prev': '上一頁',
  'case.list.next': '下一頁',
  'case.list.empty': '尚無案件，點「新增案件」建立第一筆。',
  'case.list.loadError': '無法載入案件列表',
  'case.new.title': '新增案件',
  'case.new.subtitle': '填入供應商與品項明細後送出；重量、單價以字串送出，避免浮點誤差。',
  'case.new.headerSection': '案件抬頭',
  'case.new.itemsSection': '品項明細',
  'case.new.addItem': '新增品項',
  'case.new.removeItem': '刪除',
  'case.new.submit': '建立案件',
  'case.new.submitting': '建立中…',
  'case.new.success': '案件已建立',
  'case.new.error': '建立案件失敗',
  'case.new.loadSample': '載入 EF260128 範例',
  'case.new.itemsRequired': '至少需要一筆品項',
  'case.detail.title': '案件總覽',
  'case.detail.loadError': '無法載入案件',
  'case.detail.headerCard': '案件資訊',
  'case.detail.itemsCard': '品項明細',
  'case.detail.lineNo': '#',
  'field.itemName': '品名',
  'field.declaredWeight': '重量 kg',
  'field.estimatedWeight': '概算重量 kg',
  'field.desiredUnitPrice': '希望/最低単価',
  'field.minUnitPrice': '最低単価',
  'field.quantityCount': '数量',
  'field.uom': '単位',
  'field.bagUnitWeight': '袋重 kg',
  'field.listingNotes': '上架備註',
  'field.itemNotes': '品項備註',
  'field.caseTitle': '案件抬頭',
  'field.workflowState': '流程狀態',
  'field.currencyCode': '幣別',
  'option.unitKg': 'kg',
  'option.unitBag': '袋',
  'option.unitPallet': 'パレット',
  'option.unitPiece': '件',
  'option.packFlexible': 'フレコン',
  'option.packSmallFlexible': '小フレコン',
  'option.packBara': 'バラ',
  'option.packPallet': 'パレット',
  'option.packOther': '其他',
  // --- S4 P1 一回目精算（spec §8.3：不露 cell 代碼）---
  'case.new.titlePlaceholder': '可留空，由系統自動產生案件號',
  'case.new.memberOrgLabel': '出品メンバー組織（可選）',
  'case.new.memberOrgPlaceholder': '預設為自己的組織',
  'case.detail.goP1': '前往一回目精算',
  'case.detail.venue': '会場',
  'case.detail.venueUnset': '未設定',
  'case.lifecycle.title': '案件流程推進',
  'case.venue.label': '設定会場',
  'case.venue.select': '選擇会場組織',
  'case.venue.placeholder': '請選擇会場',
  'case.venue.confirm': '確定会場',
  'case.venue.saving': '設定中…',
  'case.venue.success': '已設定会場，案件推進至「明細確定」。',
  'case.venue.error': '設定会場失敗',
  'case.venue.alreadySet': '已設定会場（組織 #{id}）。',
  'case.ship.label': '出荷確定',
  'case.ship.confirm': '出荷確定',
  'case.ship.shipping': '處理中…',
  'case.ship.notReady': '需先設定会場並完成明細確定，才能出荷。',
  'case.ship.hint': '出荷後進入「貨車磅單 / 単品重量」稽核。',
  'case.ship.success': '已出荷確定，案件推進至「出荷済」。',
  'case.ship.error': '出荷確定失敗',
  'p1.title': '一回目精算',
  'p1.subtitle': '由案件品項預填重量與最低単価，試算前受金。每次試算建立新的精算 run，不覆蓋既有結果。',
  'p1.items.title': '試算品項',
  'p1.items.empty': '此案件尚無品項，請先於案件總覽建立品項。',
  'p1.field.grossWeight': 'フ入り重量 kg',
  'p1.calc.button': '試算',
  'p1.calc.running': '試算中…',
  'p1.calc.success': 'P1 試算完成,已建立新 run',
  'p1.calc.error': 'P1 試算失敗',
  'p1.result.title': '試算結果',
  'p1.result.preTaxSum': '税別小計',
  'p1.result.inclTaxTotal': '一回目試算總額（税込）',
  'p1.result.reference50': '50% 參考前受金',
  'p1.manual.label': 'EF 實際請求金額',
  'p1.manual.hint': 'EF 依試算結果手動輸入實際請求金額;送出後寫入該 run。',
  'p1.manual.placeholder': '輸入金額（数字）',
  'p1.manual.submit': '送出',
  'p1.manual.saving': '送出中…',
  'p1.manual.saved': '已儲存',
  'p1.manual.success': 'EF 實際請求金額已記錄',
  'p1.manual.error': 'EF 實際請求金額記錄失敗',
  'p1.history.title': '試算 run 歷史',
  'p1.history.run': 'Run',
  // S3 出貨
  'shipment.current.title': '出貨現況',
  'shipment.current.empty': '尚未建立出貨計畫。',
  'shipment.current.loadError': '無法載入出貨資料',
  'shipment.plan.title': '建立 / 更新出貨計畫',
  'shipment.plan.submit': '送出出貨計畫',
  'shipment.plan.submitting': '送出中…',
  'shipment.plan.success': '出貨計畫已儲存',
  'shipment.plan.error': '出貨計畫儲存失敗',
  'shipment.field.shipmentNo': '出貨番号',
  'shipment.field.plannedShipAt': '預定出貨時間',
  'shipment.field.plannedArrivalAt': '預定到着時間',
  'shipment.field.logisticsCompany': '運送業者',
  'shipment.field.truckType': '車種',
  'shipment.field.driver': '運転手',
  'shipment.field.driverPhone': '運転手電話',
  'shipment.field.scaleRef': '磅單參照 / 備註',
  'shipment.field.scaleRefHint': '可在此填磅單編號或檔案參照；正式磅單上傳於出貨証憑步驟處理。',
  'shipment.field.truckInfoPending': '車輛資訊待補',
  // S3 荷受け
  'receiving.title': '荷受け',
  'receiving.noShipment': '尚未建立出貨計畫,請先於「出貨資料」建立出貨後再進行荷受け。',
  'receiving.weight.title': '会場過磅總重',
  'receiving.weight.submit': '記錄總重',
  'receiving.weight.submitting': '記錄中…',
  'receiving.weight.success': '会場總重已記錄',
  'receiving.weight.error': '会場總重記錄失敗',
  'receiving.details.title': '單品荷受け確認',
  'receiving.details.empty': '尚無案件品項可確認。',
  'receiving.details.submit': '記錄單品確認',
  'receiving.details.submitting': '記錄中…',
  'receiving.details.success': '單品荷受け已記錄',
  'receiving.details.error': '單品荷受け記錄失敗',
  'receiving.field.venueTotalWeight': '会場總重 kg',
  'receiving.field.measuredAt': '過磅時間',
  'receiving.field.notes': '備註',
  'receiving.field.receivedItemName': '受領品名',
  'receiving.field.receivedWeight': '受領重量 kg',
  'receiving.field.condition': '商品狀況',
  'receiving.field.conditionNotes': '狀況備註',
  'receiving.condition.asListed': '與上架相符',
  'receiving.condition.minor': '輕微異常',
  'receiving.condition.major': '重大異常',
  // S3 重量稽核（20kg 規則）
  'weightAudit.compare.title': '重量比對（メンバー vs 会場）',
  'weightAudit.compare.empty': '尚無荷受け單品資料可比對,請先完成荷受け。',
  'weightAudit.col.memberWeight': 'メンバー重量 kg',
  'weightAudit.col.venueWeight': '会場重量 kg',
  'weightAudit.col.variance': '差異 kg',
  'weightAudit.col.threshold': '閾值 kg',
  'weightAudit.col.varianceType': '差異類型',
  'weightAudit.col.status': '狀態',
  'weightAudit.col.resolution': '解決方式',
  'weightAudit.col.action': '操作',
  'weightAudit.status.useVenue': '原則採会場基準',
  'weightAudit.status.overThreshold': '超過 20kg,需報連相',
  'weightAudit.overThresholdHint': '重量差異 {kg}kg,需建立現場報連相,請陳/工藤確認;未確認前不可進入影響精算的下一步。',
  'weightAudit.create.button': '建立重量差異 review',
  'weightAudit.create.success': '已建立重量差異 review',
  'weightAudit.create.error': '建立重量差異 review 失敗',
  'weightAudit.reviews.title': '重量差異 review 清單',
  'weightAudit.reviews.empty': '尚無重量差異 review。',
  'weightAudit.state.title': '目前流程狀態',
  'weightAudit.state.current': '狀態',
  'weightAudit.truck.title': '貨車總重量（卡車磅單 vs 会場過磅）',
  'weightAudit.truck.hint': '輸入メンバー卡車総重量與会場過磅総重量；差異 ≤ 20kg 採会場基準並推進至「会場受領」，> 20kg 須三方確認。',
  'weightAudit.truck.memberTotal': 'メンバー卡車総重量（kg）',
  'weightAudit.truck.venueTotal': '会場過磅総重量（kg）',
  'weightAudit.truck.submit': '提交貨車總重量',
  'weightAudit.truck.submitting': '提交中…',
  'weightAudit.truck.accepted': '貨車總重量已採会場基準，推進至「会場受領」。',
  'weightAudit.truck.blocked': '差異超過 20kg，已建立重量差異 review，須三方確認。',
  'weightAudit.truck.error': '提交貨車總重量失敗',
  'weightAudit.item.title': '単品重量表（メンバー vs 会場）',
  'weightAudit.item.hint': '逐品輸入メンバー與会場重量並一次提交；全部差異 ≤ 20kg 推進至「重量表受領」，任一 > 20kg 須三方確認。',
  'weightAudit.item.noItems': '此案件尚無品項。',
  'weightAudit.item.submit': '提交単品重量表',
  'weightAudit.item.submitting': '提交中…',
  'weightAudit.item.accepted': '単品重量表已受領，推進至「重量表受領」。',
  'weightAudit.item.blocked': '部分品項差異超過 20kg，已建立重量差異 review，須三方確認。',
  'weightAudit.item.error': '提交単品重量表失敗',
  'weightAudit.resolve.useVenue': '採会場重量',
  'weightAudit.resolve.success': '解決方案已提出',
  'weightAudit.resolve.error': '解決方案提出失敗',

  // ---- 報連相（hourensou，spec §10 / §D5）----
  'hourensou.title': '報連相',
  'hourensou.subtitle': '20kg 重量差異、三方分配 P+Q+R≠K、工程逾期等需現場/數字/流程確認的事項，於此追蹤與解決。',
  'hourensou.empty': '尚無報連相事項。',
  'hourensou.loadError': '無法載入報連相清單',
  'hourensou.col.type': '類別',
  'hourensou.col.category': '事項',
  'hourensou.col.assignee': '負責',
  'hourensou.col.message': '內容',
  'hourensou.col.status': '狀態',
  'hourensou.col.action': '操作',
  'hourensou.type.field': '現場類',
  'hourensou.type.numeric': '數字類',
  'hourensou.type.process': '流程類',
  'hourensou.status.open': '未對應',
  'hourensou.status.acknowledged': '確認中',
  'hourensou.status.resolved': '已解決',
  'hourensou.resolve.button': '標記已解決',
  'hourensou.resolve.notePlaceholder': '解決說明（選填）',
  'hourensou.resolve.success': '報連相已標記解決',
  'hourensou.resolve.error': '報連相解決失敗',

  // ---- Freee SLA + 財務書類參照（spec §10.3 / §B4；不串接 Freee API）----
  'freee.title': 'Freee SLA / 財務書類',
  'freee.subtitle': '請求書・仕切書は精算確定から 3 営業日以内に発行（不串接 Freee API，只計時與提醒）。',
  'freee.sla.heading': 'Freee SLA 計時',
  'freee.sla.empty': '尚無 Freee SLA 計時項目。',
  'freee.sla.loadError': '無法載入 Freee SLA 清單',
  'freee.sla.col.phase': '精算階段',
  'freee.sla.col.type': '書類',
  'freee.sla.col.status': '狀態',
  'freee.sla.col.issuedAt': '發行起算',
  'freee.sla.col.dueAt': '期限（+3 営業日）',
  'freee.sla.col.action': '操作',
  'freee.sla.type.invoice': '請求書',
  'freee.sla.type.receipt': '仕切書 / 検収書',
  'freee.sla.status.pending': '計時中',
  'freee.sla.status.warning': '即將到期',
  'freee.sla.status.overdue': '已逾期',
  'freee.sla.status.completed': '已完成',
  'freee.sla.complete.button': '標記已發行',
  'freee.sla.complete.success': 'Freee task 已標記完成',
  'freee.sla.complete.error': 'Freee task 完成失敗',
  'freee.doc.heading': '財務書類參照',
  'freee.doc.empty': '尚無財務書類參照。',
  'freee.doc.loadError': '無法載入財務書類清單',
  'freee.doc.col.type': '種別',
  'freee.doc.col.freeeNo': 'freee 管理番号',
  'freee.doc.col.invoiceNo': '請求書番号',
  'freee.doc.col.statementNo': '仕切書番号',
  'freee.doc.col.note': '備註',
  'freee.doc.form.heading': '記録財務書類參照',
  'freee.doc.form.docType': '種別',
  'freee.doc.form.freeeNo': 'freee 管理番号',
  'freee.doc.form.invoiceNo': '請求書番号',
  'freee.doc.form.statementNo': '仕切書番号',
  'freee.doc.form.attachment': '附件參照',
  'freee.doc.form.note': '備註',
  'freee.doc.form.submit': '記録',
  'freee.doc.form.success': '財務書類參照已記録',
  'freee.doc.form.error': '財務書類參照記録失敗',
  'freee.rbac.forbidden': '無權限檢視 / 操作此區塊。',

  // ---- 入札管理（auction workspace, spec §F）----
  'auction.title': '入札管理',
  'auction.subtitle': '開標、入札者審核、下見預約、入札、ranking、落札確定。',
  'auction.tab.round': '入札資料／公開',
  'auction.tab.bidders': '入札者登録',
  'auction.tab.preview': '下見預約',
  'auction.tab.bids': '入札提交',
  'auction.tab.ranking': 'Ranking',
  'auction.tab.awards': '落札確定',
  'auction.round.title': '入札窓口',
  'auction.round.opensAt': '入札開始',
  'auction.round.cutoffAt': '入札締切',
  'auction.round.status': '狀態',
  'auction.round.closedAt': '締切時刻',
  'auction.round.open': '開標',
  'auction.round.opening': '開標中',
  'auction.round.close': '締切',
  'auction.round.closing': '締切中',
  'auction.round.openSuccess': '已開標',
  'auction.round.openError': '開標失敗',
  'auction.round.closeSuccess': '已締切',
  'auction.round.closeError': '締切失敗',
  'auction.round.none': '尚未開標。',
  'auction.round.current': '目前窓口',
  'auction.bidders.title': '入札者一覧',
  'auction.bidders.orgId': '入札者組織 ID',
  'auction.bidders.invitedAt': '招待時刻',
  'auction.bidders.empty': '尚無受邀入札者。',
  'auction.bidders.register': '邀請入札者',
  'auction.bidders.registering': '邀請中',
  'auction.bidders.orgPlaceholder': '入札者組織 ID',
  'auction.bidders.registerSuccess': '已邀請入札者',
  'auction.bidders.registerError': '邀請入札者失敗',
  'auction.bidders.hint': '未受邀（未審核通過）的組織不可入札或預約下見。',
  'auction.preview.title': '下見枠',
  'auction.preview.startAt': '開始',
  'auction.preview.endAt': '結束',
  'auction.preview.capacity': '容量',
  'auction.preview.status': '狀態',
  'auction.preview.create': '新增下見枠',
  'auction.preview.creating': '建立中',
  'auction.preview.createSuccess': '已建立下見枠',
  'auction.preview.createError': '建立下見枠失敗',
  'auction.preview.empty': '尚無下見枠。',
  'auction.preview.book': '預約',
  'auction.preview.booking': '預約中',
  'auction.preview.bookSuccess': '已預約下見',
  'auction.preview.bookError': '預約下見失敗',
  'auction.preview.capacityHint': '容量預設 1，不可超額重複預約。',
  'auction.bids.title': '入札提交',
  'auction.bids.item': '品項',
  'auction.bids.unitPrice': '入札単価',
  'auction.bids.unitPricePlaceholder': '単価（円）',
  'auction.bids.submit': '提交入札',
  'auction.bids.submitting': '提交中',
  'auction.bids.submitSuccess': '已提交入札',
  'auction.bids.submitError': '提交入札失敗',
  'auction.bids.closedHint': '入札締切後不可再提交。',
  'auction.bids.selectItem': '請選擇品項',
  'auction.ranking.title': 'Ranking（逐品）',
  'auction.ranking.selectItem': '選擇品項以檢視 ranking',
  'auction.ranking.rank': '順位',
  'auction.ranking.bidder': '入札者組織',
  'auction.ranking.unitPrice': '入札単価',
  'auction.ranking.submittedAt': '提交時刻',
  'auction.ranking.empty': '此品項尚無有效入札。',
  'auction.ranking.tieHint': '排序：単価高→低；同価以先提交者優先。',
  'auction.awards.title': '落札確定',
  'auction.awards.item': '品項',
  'auction.awards.winningBid': '落札する入札',
  'auction.awards.confirm': '確定落札',
  'auction.awards.confirming': '確定中',
  'auction.awards.confirmSuccess': '已確定落札',
  'auction.awards.confirmError': '確定落札失敗',
  'auction.awards.confirmedTitle': '已確定落札一覧',
  'auction.awards.empty': '尚無確定落札。',
  'auction.awards.unitPrice': '落札単価',
  'auction.awards.awardee': '落札業者組織',
  'auction.awards.confirmedAt': '確定時刻',
  'auction.awards.feedsP2': '落札単価將作為二回目精算（P2）三方分配的基準。',
  'auction.awards.closeFirst': '請先締切入札窓口再確定落札。',

  // ---- メンバー決策（/cases/:id/adjustments）------------------------------
  'adjust.title': 'メンバー決策',
  'adjust.subtitle': '落札単価低於希望/最低単価時，メンバー代表逐品三選一：希望調整、返品、預り。',
  'adjust.gateHint': '落札単価 ≥ 希望/最低単価的品項毋須決策；返品/預り 僅記錄不結算（Q5/Q6 未 sign-off）。',
  'adjust.item': '品項',
  'adjust.minPrice': '希望/最低単価',
  'adjust.auctionPrice': '落札単価',
  'adjust.needsDecision': '需決策',
  'adjust.noNeed': '毋須決策',
  'adjust.decisionType': '決策種別',
  'adjust.type.priceAdjust': '希望調整',
  'adjust.type.return': '返品',
  'adjust.type.hold': '預り',
  'adjust.adjustedPrice': 'メンバー調整後単価',
  'adjust.adjustedPriceHint': 'J − M 須介於 5〜20 円（J=落札単価, M=調整後単価）。',
  'adjust.gap': 'J − M 差額',
  'adjust.reason': '理由',
  'adjust.holdLocation': '預り場所',
  'adjust.holdDeadline': '預り期限',
  'adjust.reasonPlaceholder': '請輸入理由（選填）',
  'adjust.holdLocationPlaceholder': '預り場所',
  'adjust.holdDeadlinePlaceholder': '預り期限（如 2026-07-31）',
  'adjust.submit': '記錄決策',
  'adjust.submitting': '記錄中',
  'adjust.success': '已記錄メンバー決策',
  'adjust.error': '記錄決策失敗',
  'adjust.cancel': '取消',
  'adjust.cancelling': '取消中',
  'adjust.cancelSuccess': '已取消決策',
  'adjust.cancelError': '取消決策失敗',
  'adjust.errGapRange': 'J − M 須介於 5〜20 円',
  'adjust.errAdjustedRequired': '希望調整須輸入調整後単価',
  'adjust.form.title': '新增決策',
  'adjust.list.title': '已記錄決策',
  'adjust.list.empty': '尚無メンバー決策。',
  'adjust.status': '狀態',
  'adjust.decidedAt': '決策時刻',
  'adjust.selectItem': '選擇品項',

  // ---- 三方分配（/cases/:id/shares，P+Q+R=K）------------------------------
  'shares.title': '三方分配',
  'shares.subtitle': '以 K = 落札単価 − メンバー調整後単価 為基準，EF 20%／メンバー 40%／会場 40%，且 P + Q + R = K。',
  'shares.ratioHint': '預設比率：EF 20%、メンバー 40%、会場 40%（整數単価，扣減保等式）。',
  'shares.item': '品項',
  'shares.j': '落札単価',
  'shares.m': 'メンバー調整後単価',
  'shares.k': 'K（差額）',
  'shares.p': 'P（EF 20%）',
  'shares.q': 'Q（メンバー 40%）',
  'shares.r': 'R（会場 40%）',
  'shares.sum': 'P + Q + R',
  'shares.check': '等式檢查',
  'shares.ok': '一致',
  'shares.mismatch': '不一致',
  'shares.mismatchHint': 'P + Q + R ≠ K — 需數字類報連相確認後才能進入二回目精算。',
  'shares.source': '分配數值來自最新 P2 run；尚無 run 時以預設 20/40/40 試算顯示。',
  'shares.empty': '尚無可分配品項（需先完成落札確定與メンバー決策）。',
  'shares.preview': '預覽（預設比率）',
  'shares.fromRun': '來自 P2 run',
  'shares.loadError': '載入三方分配資料失敗',

  // ---- 二回目精算（/cases/:id/settlements/p2）-----------------------------
  'p2.title': '二回目精算',
  'p2.subtitle': '逐品輸入二回目（会場荷受け）重量，套用三方分配後試算二回目総額；T38 EF 實際請求金額手入力。',
  'p2.items.title': '二回目試算品項',
  'p2.items.empty': '尚無可試算品項（需先完成落札確定與メンバー決策）。',
  'p2.field.weight': '二回目重量（kg）',
  'p2.field.j': '落札単価',
  'p2.field.m': 'メンバー調整後単価',
  'p2.field.k': 'K',
  'p2.field.p': 'P（EF）',
  'p2.field.q': 'Q（メンバー）',
  'p2.field.r': 'R（会場）',
  'p2.shareHint': '不填 P/Q/R 時後端以預設 20/40/40 分配；填則三者皆須填且 P+Q+R=K。',
  'p2.calc.button': '試算',
  'p2.calc.running': '試算中',
  'p2.calc.success': '二回目試算完成',
  'p2.calc.error': '二回目試算失敗',
  'p2.result.title': '二回目試算結果',
  'p2.result.total': '二回目試算総額',
  'p2.result.estimate': '二回目請求概算',
  'p2.result.itemsTitle': '逐品明細',
  'p2.result.sUnit': '二回目単価',
  'p2.result.tSubtotal': '品項小計',
  'p2.manual.label': 'EF 實際請求金額（手入力）',
  'p2.manual.hint': '對最新試算 run 手入力 EF 實際請求金額（T38）。',
  'p2.manual.placeholder': '請輸入金額',
  'p2.manual.submit': '保存',
  'p2.manual.saving': '保存中',
  'p2.manual.saved': '已保存',
  'p2.manual.success': '已保存 EF 實際請求金額',
  'p2.issue.cardTitle': '発行 → 入金確認',
  'p2.issue.currentState': '目前狀態',
  'p2.issue.label': '二回目 発行',
  'p2.issue.button': '発行',
  'p2.issue.running': '発行中…',
  'p2.issue.hint': '発行後進入「入金確認」。',
  'p2.issue.needT38': '需先試算並輸入 T38（EF 實際請求金額）才能発行。',
  'p2.issue.notReady': '尚未達可発行狀態（需完成試算與 T38）。',
  'p2.issue.success': '已発行二回目精算，案件推進至「二回目発行済」。',
  'p2.issue.error': '発行失敗',
  'p2.payment.label': '二回目 入金確認',
  'p2.payment.button': '入金確認',
  'p2.payment.running': '確認中…',
  'p2.payment.notReady': '需先発行才能入金確認。',
  'p2.payment.success': '已入金確認，案件推進至「二回目入金確認済」。',
  'p2.payment.error': '入金確認失敗',
  'p2.manual.error': '保存失敗',
  'p2.history.title': '試算歷史',
  'p2.history.run': 'Run',
  'p2.latest.title': '目前最新 run',
  'p2.latest.none': '尚無 P2 run。',
  // ---- S7 最終重量（final-weight）----
  'finalWeight.title': '最終重量',
  'finalWeight.subtitle': '逐段呈現朝日出貨重量、落札業者入荷重量與最終採用重量，並計算差異。',
  'finalWeight.state.title': '狀態與受領推進',
  'finalWeight.state.current': '目前狀態',
  'finalWeight.state.lockedHint': '案件已結案鎖定，最終重量唯讀，不可再推進。',
  'finalWeight.state.notReady': '尚未進入可受領最終重量的階段（需先完成二回目入金確認）。',
  'finalWeight.receive.button': '確認最終重量受領',
  'finalWeight.receive.running': '受領中',
  'finalWeight.receive.hint': '確認後進入最終報告階段（FinalWeightReceived）。',
  'finalWeight.receive.blockedByVariance': '尚有最終重量差異超過 20kg，請先於重量稽核完成現場確認再受領。',
  'finalWeight.receive.success': '已確認最終重量受領',
  'finalWeight.receive.error': '最終重量受領失敗',
  'finalWeight.compare.title': '逐段重量對照',
  'finalWeight.compare.empty': '尚無荷受け重量資料。',
  'finalWeight.compare.unavailable': '尚無法取得出貨/荷受け重量資料。',
  'finalWeight.col.memberWeight': 'メンバー申告重量',
  'finalWeight.col.venueWeight': '朝日出貨/会場重量',
  'finalWeight.col.finalWeight': '最終採用重量',
  'finalWeight.col.variance': '差異（kg）',
  'finalWeight.col.status': '狀態',
  'finalWeight.status.ok': '原則採会場基準',
  'finalWeight.status.overThreshold': '差異 > 20kg',
  'finalWeight.overThresholdHint': '最終重量與前段差異約 {kg}kg，已超過 20kg，須建立現場報連相由陳/工藤確認，前端不自動裁定權威重量。',
  'finalWeight.snapshot.none': '目前無獨立最終重量 snapshot（以会場荷受け重量為採用值）。',
  'finalWeight.reviews.title': '相關重量差異 review',
  'finalWeight.reviews.empty': '尚無重量差異 review。',
  // ---- S7 三回目精算 P3（disabled by design）----
  'p3.title': '三回目精算',
  'p3.subtitle': '此功能設計上停用，等待業主確認 W41/W40 公式後啟用。',
  'p3.blocked.title': '三回目精算尚未開放',
  'p3.blocked.body': '三回目精算（W41/W40）公式尚未取得業主 sign-off，依規範本頁停用，系統不進行任何三回目計算。',
  'p3.blocked.q2': '對應未解 BLOCKER：Q2（三回目精算公式，工程規格書 §6.5）。',
  'p3.formula.title': '公式 placeholder（唯讀）',
  'p3.formula.hint': '以下為待確認公式欄位，僅供對照，未填入實際係數。',
  'p3.formula.cell': '欄位',
  'p3.formula.purpose': '用途',
  'p3.formula.value': '值',
  'p3.formula.pending': '待業主確認',
  'p3.formula.w41': 'W41 三回目最終精算額',
  'p3.formula.w41Desc': '三回目最終精算總額（公式待確認）。',
  'p3.formula.w40': 'W40 三回目差異調整',
  'p3.formula.w40Desc': '最終重量差異對精算額的調整（公式待確認）。',
  'p3.calc.button': '試算',
  'p3.calc.disabledHint': '試算停用中：待業主確認三回目公式後開放。',
  'p3.probe.title': '驗證後端守門',
  'p3.probe.hint': '呼叫三回目精算 API 以證明後端對未確認公式的阻擋（預期回 blocked，不顯示任何計算結果）。',
  'p3.probe.button': '驗證 API 回應 blocked',
  'p3.probe.running': '驗證中',
  'p3.probe.done': '後端已回應，確認三回目仍受阻擋。',
  'p3.probe.confirmedBlocked': '已確認：後端阻擋三回目精算（公式未確認）。',
  'p3.probe.blockedGeneric': '後端回應已阻擋（非預期錯誤碼）',
  'p3.probe.unexpected': '異常：三回目竟回成功，請立即停下確認（不應發生）。',
  // ---- S8 最終報告 + 結案（final-report）----
  'finalReport.title': '最終報告 / 結案',
  'finalReport.subtitle': '呈現報告 snapshot 與三方確認，確認後結案進入 Closed_Locked。',
  'finalReport.locked.badge': '已鎖定',
  'finalReport.locked.title': '案件已結案鎖定',
  'finalReport.locked.body': '案件已進入 Closed_Locked，業務資料 100% 不可修改，本頁為唯讀。',
  'finalReport.snapshot.title': '報告 snapshot',
  'finalReport.snapshot.subject': '案件名稱',
  'finalReport.snapshot.state': '目前狀態',
  'finalReport.snapshot.none': '目前無獨立最終報告 snapshot（以案件主檔與狀態呈現）。',
  'finalReport.snapshot.hash': '報告雜湊（blake3）',
  'finalReport.confirm.title': '三方確認',
  'finalReport.confirm.hint': '結案前由 EF、億成（メンバー）、朝日（会場）三方確認最終報告。',
  'finalReport.confirm.confirmed': '已確認',
  'finalReport.confirm.pending': '待確認',
  'finalReport.confirm.button': '確認',
  'finalReport.confirm.success': '已記錄確認',
  'finalReport.confirm.error': '確認失敗',
  'finalReport.confirm.allConfirmed': '三方已全數確認，可結案。',
  'finalReport.confirm.progress': '三方確認進度：{n}/{total}。',
  'finalReport.role.ef': 'EF（中間商）',
  'finalReport.role.yicheng': '億成（メンバー）',
  'finalReport.role.asahi': '朝日（会場）',
  'finalReport.precondition.title': '結案前置條件',
  'finalReport.precondition.none': '無重量差異 review，結案前置條件已滿足。',
  'finalReport.precondition.allResolved': '所有重量差異 review 均已解決，結案前置條件已滿足。',
  'finalReport.precondition.blocked': '尚有 {n} 筆重量差異 review 未解決，須先完成現場確認才能結案。',
  'finalReport.actions.title': '推進動作',
  'finalReport.review.label': '開啟最終報告審查',
  'finalReport.review.button': '開啟最終報告審查',
  'finalReport.review.running': '處理中',
  'finalReport.review.notReady': '尚未進入可開啟審查的階段（需先完成最終重量受領）。',
  'finalReport.review.success': '已開啟最終報告審查',
  'finalReport.review.error': '開啟最終報告審查失敗',
  'finalReport.close.label': '結案鎖定',
  'finalReport.close.button': '結案並鎖定（Closed_Locked）',
  'finalReport.close.running': '結案中',
  'finalReport.close.hint': '結案後案件進入 Closed_Locked，業務資料將不可再修改。',
  'finalReport.close.notReady': '尚未進入可結案的階段（需先開啟最終報告審查）。',
  'finalReport.close.blockedByVariance': '尚有未解決的重量差異 review，無法結案。',
  'finalReport.close.blockedByConfirm': '三方確認尚未全數完成，無法結案。',
  'finalReport.close.success': '案件已結案鎖定',
  'finalReport.close.error': '結案失敗',

  // ---- 2FA / TOTP（spec §A1 設定頁 + §9.2 step-up）----
  'nav.security': '帳號安全',
  'desc.security': '管理二要素認証（2FA / TOTP），高風險操作需 step-up 驗證。',
  'twofa.title': '二要素認証（2FA）',
  'twofa.subtitle': '啟用後，精算手入力、結案、落札確定、重量裁定等高風險操作需再輸入一次性驗證碼。',
  'twofa.status': '目前狀態',
  'twofa.enabled': '已啟用',
  'twofa.disabled': '未啟用',
  'twofa.codePlaceholder': '6 位驗證碼',
  'twofa.setup.intro': '使用 Google Authenticator / Authy 等 App 掃描或手動輸入金鑰，啟用 2FA。',
  'twofa.setup.start': '開始設定 2FA',
  'twofa.setup.starting': '產生中…',
  'twofa.setup.ready': '已產生金鑰，請以 App 掃描後輸入驗證碼。',
  'twofa.setup.error': '產生 2FA 金鑰失敗',
  'twofa.setup.scan': '掃描 QR 或手動輸入金鑰',
  'twofa.setup.scanHint': '將以下 otpauth URI 加入 authenticator App（多數 App 支援貼上 URI 或手動輸入金鑰）。',
  'twofa.setup.secret': '金鑰',
  'twofa.verify.label': '輸入 App 顯示的 6 位驗證碼',
  'twofa.verify.submit': '驗證並啟用',
  'twofa.verify.submitting': '驗證中…',
  'twofa.verify.success': '已啟用 2FA',
  'twofa.verify.error': '驗證碼錯誤或已過期',
  'twofa.disable.intro': '停用 2FA 需輸入一組目前有效的驗證碼。停用後高風險操作將不再要求 step-up。',
  'twofa.disable.label': '輸入 6 位驗證碼以停用',
  'twofa.disable.submit': '停用 2FA',
  'twofa.disable.submitting': '停用中…',
  'twofa.disable.success': '已停用 2FA',
  'twofa.disable.error': '停用失敗（驗證碼錯誤或未啟用）',
  // step-up 對話框
  'stepUp.title': '需要二要素驗證',
  'stepUp.desc': '此為高風險操作，請輸入 authenticator App 顯示的一次性驗證碼以繼續。',
  'stepUp.codeLabel': '一次性驗證碼',
  'stepUp.codePlaceholder': '6 位驗證碼',
  'stepUp.hint': '若尚未啟用 2FA，請先到「帳號安全」啟用。',
  'stepUp.confirm': '確認並送出',
  // 後端回傳的 message_key（error envelope）
  'iam.error.step_up_required': '此操作需要先啟用 2FA 並提供一次性驗證碼。',
  'iam.error.step_up_invalid': '二要素驗證碼錯誤或已過期。',
  'iam.totp.enabled': '已啟用 2FA。',
  'iam.totp.disabled': '已停用 2FA。',
}

const ja: Dictionary = {
  ...zhTW,
  // --- 品項明細編集（/cases/:id/items）---
  'case.items.title': '品目明細',
  'case.items.subtitle': '品名・概算重量・最低/希望単価・荷姿を維持。品目ごとに編集可（価格バージョンはバックエンドが自動記録）。',
  'case.items.editTitle': '品目を編集',
  'case.items.editAction': '編集',
  'case.items.lockedHint': '案件は確定ロック済（Closed_Locked）。品目は編集できません。',
  'case.items.loadError': '品目を読み込めません',
  'case.items.success': '品目を更新しました',
  'case.items.error': '品目の更新に失敗しました',
  'case.items.empty': 'この案件にはまだ品目がありません。',
  'case.items.reasonHint': '変更理由（価格バージョンに記録。空欄可）。',
  'common.save': '保存',
  'field.targetUnitPrice': '希望単価',
  'field.packageForm': '荷姿',
  'field.reason': '理由',
  'auth.signIn': 'ログイン',
  'auth.signOut': 'ログアウト',
  'nav.dashboard': 'ダッシュボード',
  'nav.masterData': 'マスタ管理',
  'nav.partners': '取引先',
  'nav.cases': '案件管理',
  'nav.receiving': '入出荷',
  'nav.inventory': '在庫',
  'nav.sales': '入札',
  'nav.settlements': '精算',
  'nav.admin': 'システム管理',
  'nav.adminUsers': 'ユーザー管理',
  'nav.adminRoles': '権限管理',
  'nav.reports': 'レポート',
  'nav.audit': '監査',
  'nav.notifications': '通知',
  // ---- レポートセンター（/reports）---------------------------------------
  'reports.title': 'レポートセンター',
  'reports.subtitle': '案件進捗・重量差異・精算・入札・Freee SLA・監査を一元照会してエクスポート。',
  'reports.tab.casesProgress': '案件進捗',
  'reports.tab.weight': '重量差異',
  'reports.tab.settlements': '精算',
  'reports.tab.auctions': '入札',
  'reports.tab.freeeSla': 'Freee SLA',
  'reports.tab.audit': '監査',
  'reports.filter.from': '開始日',
  'reports.filter.to': '終了日',
  'reports.filter.status': 'ステータス',
  'reports.filter.statusAll': 'すべて',
  'reports.filter.statusPlaceholder': 'ステータス（完全一致）',
  'reports.filter.caseNo': '案件番号',
  'reports.filter.caseNoPlaceholder': '案件番号（完全一致）',
  'reports.filter.apply': '適用',
  'reports.filter.reset': 'クリア',
  'reports.exportCsv': 'CSV エクスポート',
  'reports.export.empty': 'エクスポート可能なデータがありません',
  'reports.loadError': 'レポートを読み込めません',
  'reports.empty': 'この条件のデータはありません',
  'reports.total': '全 {total} 件',
  'reports.prev': '前へ',
  'reports.next': '次へ',
  'reports.page': '{page} ページ',
  'reports.col.caseNo': '案件番号',
  'reports.col.title': '案件名',
  'reports.col.workflowState': '業務状態',
  'reports.col.memberOrg': '出品メンバー組織',
  'reports.col.venueOrg': '会場組織',
  'reports.col.openExceptions': '未解決の異常',
  'reports.col.nextStep': '次のステップ',
  'reports.col.createdAt': '作成日時',
  'reports.col.reviewId': 'Review ID',
  'reports.col.caseId': '案件 ID',
  'reports.col.itemName': '単品名',
  'reports.col.varianceType': '差異種別',
  'reports.col.memberWeight': 'メンバー重量（kg）',
  'reports.col.venueWeight': '会場重量（kg）',
  'reports.col.finalWeight': '確定重量（kg）',
  'reports.col.varianceKg': '差異（kg）',
  'reports.col.thresholdKg': '閾値（kg）',
  'reports.col.status': 'ステータス',
  'reports.col.p1IssuedTotal': '一回目試算総額（税込）',
  'reports.col.p1Reference50': '一回目 50% 参考前受金',
  'reports.col.p1ActualRequest': '一回目 EF 実際請求',
  'reports.col.p1Status': '一回目ステータス',
  'reports.col.p2PreTaxTotal': '二回目試算総額（税抜）',
  'reports.col.p2ProfitShare': '二回目利益分配額',
  'reports.col.p2ActualRequest': '二回目 EF 実際請求',
  'reports.col.p2Status': '二回目ステータス',
  'reports.col.p3Status': '三回目ステータス',
  'reports.col.settlementNone': '—',
  'reports.col.biddersCount': '入札参加者数',
  'reports.col.bidsCount': '入札件数',
  'reports.col.awardsCount': '落札確定数',
  'reports.col.awardedUnitPrice': '落札単価',
  'reports.col.awardedConfirmedAt': '落札確定日時',
  'reports.col.taskId': 'Task ID',
  'reports.col.settlementPhase': '精算回',
  'reports.col.taskType': '書類種別',
  'reports.col.issuedAt': '発行日時',
  'reports.col.warningAt': '警告期限',
  'reports.col.dueAt': '期限',
  'reports.col.completedAt': '完了日時',
  'reports.col.auditId': 'ID',
  'reports.col.actorUser': '操作者 User',
  'reports.col.organization': '組織 ID',
  'reports.col.eventName': 'イベント名',
  'reports.col.resourceType': 'リソース型',
  'reports.col.resourceId': 'リソース ID',
  'reports.col.beforeSummary': '変更前要約',
  'reports.col.afterSummary': '変更後要約',
  'desc.adminRoles': 'ロールと権限のバインディングを案件フローと分離して管理。',
  'common.underConstruction': '施工中',
  'common.underConstructionDesc': 'この画面は構築中です。ルートとナビは準備済み、機能は後日公開。',
  'case.new': '案件新規作成',
  'rbac.forbidden': '権限不足',
  'rbac.forbiddenTitle': 'このページにアクセスできません',
  'rbac.forbiddenDesc': 'お使いのロールにはこの機能へのアクセス権がありません。管理者にお問い合わせください。',
  'rbac.disabledTooltip': 'この機能は未公開です。オーナー確認後に有効化されます。',
  'case.list.title': '案件一覧',
  'case.list.subtitle': '入札コラボ案件の検索・閲覧。行をクリックで案件ワークスペースへ。',
  'case.list.itemCount': '品目数',
  'case.list.createdAt': '作成日時',
  'case.list.searchSeller': '出品者で検索',
  'case.list.searchCaseNo': '案件番号で検索',
  'case.list.prev': '前へ',
  'case.list.next': '次へ',
  'case.list.empty': '案件がありません。「案件新規作成」から登録してください。',
  'case.list.loadError': '案件一覧を取得できませんでした',
  'case.new.title': '案件新規作成',
  'case.new.subtitle': '出品者と品目を入力して送信。重量・単価は文字列で送り、浮動小数を避けます。',
  'case.new.headerSection': '案件ヘッダー',
  'case.new.itemsSection': '品目明細',
  'case.new.addItem': '品目を追加',
  'case.new.removeItem': '削除',
  'case.new.submit': '案件を作成',
  'case.new.submitting': '作成中…',
  'case.new.success': '案件を作成しました',
  'case.new.error': '案件の作成に失敗しました',
  'case.new.loadSample': 'EF260128 サンプルを読込',
  'case.new.itemsRequired': '品目は最低1件必要です',
  'case.detail.title': '案件概要',
  'case.detail.loadError': '案件を取得できませんでした',
  'case.detail.headerCard': '案件情報',
  'case.detail.itemsCard': '品目明細',
  'field.itemName': '品名',
  'field.declaredWeight': '重量 kg',
  'field.estimatedWeight': '概算重量 kg',
  'field.desiredUnitPrice': '希望/最低単価',
  'field.minUnitPrice': '最低単価',
  'field.quantityCount': '数量',
  'field.uom': '単位',
  'field.bagUnitWeight': '袋重 kg',
  'field.listingNotes': '出品メモ',
  'field.itemNotes': '品目メモ',
  'field.caseTitle': '案件タイトル',
  'field.workflowState': 'ワークフロー状態',
  'field.currencyCode': '通貨',
  'option.packFlexible': 'フレコン',
  'option.packSmallFlexible': '小フレコン',
  'option.packBara': 'バラ',
  'option.packPallet': 'パレット',
  'option.packOther': 'その他',
  // S4 P1 一回目精算
  'case.new.titlePlaceholder': '空欄可。システムが案件番号を自動採番します',
  'case.new.memberOrgLabel': '出品メンバー組織（任意）',
  'case.new.memberOrgPlaceholder': '未選択時は自組織が対象',
  'case.detail.goP1': '一回目精算へ',
  'case.detail.venue': '会場',
  'case.detail.venueUnset': '未設定',
  'case.lifecycle.title': '案件ライフサイクル操作',
  'case.venue.label': '会場設定',
  'case.venue.select': '会場組織を選択',
  'case.venue.placeholder': '会場を選択してください',
  'case.venue.confirm': '会場を確定',
  'case.venue.saving': '設定中…',
  'case.venue.success': '会場を設定しました。案件は「明細確定」に進みました。',
  'case.venue.error': '会場設定に失敗しました',
  'case.venue.alreadySet': '会場設定済み（組織 #{id}）。',
  'case.ship.label': '出荷確定',
  'case.ship.confirm': '出荷確定',
  'case.ship.shipping': '処理中…',
  'case.ship.notReady': '会場設定と明細確定の完了後に出荷できます。',
  'case.ship.hint': '出荷後、「トラック計量 / 単品重量」確認へ進みます。',
  'case.ship.success': '出荷を確定しました。案件は「出荷済」に進みました。',
  'case.ship.error': '出荷確定に失敗しました',
  'p1.title': '一回目精算',
  'p1.subtitle': '案件品目から重量と最低単価をプリフィルし、前受金を試算します。試算ごとに新しい精算 run を作成し、既存結果は上書きしません。',
  'p1.items.title': '試算品目',
  'p1.items.empty': 'この案件には品目がありません。先に案件概要で品目を作成してください。',
  'p1.field.grossWeight': 'フ入り重量 kg',
  'p1.calc.button': '試算',
  'p1.calc.running': '試算中…',
  'p1.calc.success': 'P1 試算完了、新しい run を作成しました',
  'p1.calc.error': 'P1 試算に失敗しました',
  'p1.result.title': '試算結果',
  'p1.result.preTaxSum': '税別小計',
  'p1.result.inclTaxTotal': '一回目試算総額（税込）',
  'p1.result.reference50': '50% 参考前受金',
  'p1.manual.label': 'EF 実際請求金額',
  'p1.manual.hint': 'EF が試算結果を基に実際の請求金額を手入力します。送信後、該当 run に記録されます。',
  'p1.manual.placeholder': '金額を入力（数字）',
  'p1.manual.submit': '送信',
  'p1.manual.saving': '送信中…',
  'p1.manual.saved': '保存済み',
  'p1.manual.success': 'EF 実際請求金額を記録しました',
  'p1.manual.error': 'EF 実際請求金額の記録に失敗しました',
  'p1.history.title': '試算 run 履歴',
  'p1.history.run': 'Run',
  // S3 出荷
  'shipment.current.title': '出荷状況',
  'shipment.current.empty': '出荷計画は未作成です。',
  'shipment.current.loadError': '出荷情報を取得できませんでした',
  'shipment.plan.title': '出荷計画の作成 / 更新',
  'shipment.plan.submit': '出荷計画を送信',
  'shipment.plan.submitting': '送信中…',
  'shipment.plan.success': '出荷計画を保存しました',
  'shipment.plan.error': '出荷計画の保存に失敗しました',
  'shipment.field.shipmentNo': '出荷番号',
  'shipment.field.plannedShipAt': '出荷予定日時',
  'shipment.field.plannedArrivalAt': '到着予定日時',
  'shipment.field.logisticsCompany': '運送業者',
  'shipment.field.truckType': '車種',
  'shipment.field.driver': '運転手',
  'shipment.field.driverPhone': '運転手電話',
  'shipment.field.scaleRef': '計量伝票参照 / 備考',
  'shipment.field.scaleRefHint': '計量伝票番号やファイル参照を記入できます。正式な伝票アップロードは出荷証憑ステップで処理します。',
  'shipment.field.truckInfoPending': '車両情報は後日',
  // S3 荷受け
  'receiving.title': '荷受け',
  'receiving.noShipment': '出荷計画が未作成です。先に「出荷資料」で出荷を作成してから荷受けを行ってください。',
  'receiving.weight.title': '会場計量総重量',
  'receiving.weight.submit': '総重量を記録',
  'receiving.weight.submitting': '記録中…',
  'receiving.weight.success': '会場総重量を記録しました',
  'receiving.weight.error': '会場総重量の記録に失敗しました',
  'receiving.details.title': '品目荷受け確認',
  'receiving.details.empty': '確認できる案件品目がありません。',
  'receiving.details.submit': '品目確認を記録',
  'receiving.details.submitting': '記録中…',
  'receiving.details.success': '品目荷受けを記録しました',
  'receiving.details.error': '品目荷受けの記録に失敗しました',
  'receiving.field.venueTotalWeight': '会場総重量 kg',
  'receiving.field.measuredAt': '計量日時',
  'receiving.field.notes': '備考',
  'receiving.field.receivedItemName': '受領品名',
  'receiving.field.receivedWeight': '受領重量 kg',
  'receiving.field.condition': '商品状態',
  'receiving.field.conditionNotes': '状態備考',
  'receiving.condition.asListed': '出品どおり',
  'receiving.condition.minor': '軽微な異常',
  'receiving.condition.major': '重大な異常',
  // S3 重量監査（20kg ルール）
  'weightAudit.compare.title': '重量比較（メンバー vs 会場）',
  'weightAudit.compare.empty': '比較できる荷受け品目がありません。先に荷受けを完了してください。',
  'weightAudit.col.memberWeight': 'メンバー重量 kg',
  'weightAudit.col.venueWeight': '会場重量 kg',
  'weightAudit.col.variance': '差異 kg',
  'weightAudit.col.threshold': '閾値 kg',
  'weightAudit.col.varianceType': '差異種別',
  'weightAudit.col.status': '状態',
  'weightAudit.col.resolution': '解決方法',
  'weightAudit.col.action': '操作',
  'weightAudit.status.useVenue': '原則として会場基準を採用',
  'weightAudit.status.overThreshold': '20kg 超過、報連相が必要',
  'weightAudit.overThresholdHint': '重量差異 {kg}kg、現場報連相を作成し、陳/工藤の確認が必要です。確認前は精算に影響する次工程へ進めません。',
  'weightAudit.create.button': '重量差異 review を作成',
  'weightAudit.create.success': '重量差異 review を作成しました',
  'weightAudit.create.error': '重量差異 review の作成に失敗しました',
  'weightAudit.reviews.title': '重量差異 review 一覧',
  'weightAudit.reviews.empty': '重量差異 review はありません。',
  'weightAudit.state.title': '現在のワークフロー状態',
  'weightAudit.state.current': '状態',
  'weightAudit.truck.title': 'トラック総重量（トラック計量 vs 会場計量）',
  'weightAudit.truck.hint': 'メンバーのトラック総重量と会場計量総重量を入力。差異 ≤ 20kg は会場基準で「会場受領」へ進み、> 20kg は三者確認が必要です。',
  'weightAudit.truck.memberTotal': 'メンバートラック総重量（kg）',
  'weightAudit.truck.venueTotal': '会場計量総重量（kg）',
  'weightAudit.truck.submit': 'トラック総重量を送信',
  'weightAudit.truck.submitting': '送信中…',
  'weightAudit.truck.accepted': 'トラック総重量を会場基準で受け入れ、「会場受領」へ進みました。',
  'weightAudit.truck.blocked': '差異が 20kg を超過。重量差異 review を作成しました。三者確認が必要です。',
  'weightAudit.truck.error': 'トラック総重量の送信に失敗しました',
  'weightAudit.item.title': '単品重量表（メンバー vs 会場）',
  'weightAudit.item.hint': '品目ごとにメンバーと会場の重量を入力し一括送信。全差異 ≤ 20kg で「重量表受領」へ進み、いずれか > 20kg は三者確認が必要です。',
  'weightAudit.item.noItems': 'この案件には品目がありません。',
  'weightAudit.item.submit': '単品重量表を送信',
  'weightAudit.item.submitting': '送信中…',
  'weightAudit.item.accepted': '単品重量表を受領し、「重量表受領」へ進みました。',
  'weightAudit.item.blocked': '一部品目の差異が 20kg を超過。重量差異 review を作成しました。三者確認が必要です。',
  'weightAudit.item.error': '単品重量表の送信に失敗しました',
  'weightAudit.resolve.useVenue': '会場重量を採用',
  'weightAudit.resolve.success': '解決方法を提出しました',
  'weightAudit.resolve.error': '解決方法の提出に失敗しました',

  // ---- 入札管理（auction workspace）----
  'auction.title': '入札管理',
  'auction.subtitle': '開札・入札者審査・下見予約・入札・ランキング・落札確定。',
  'auction.tab.round': '入札情報／公開',
  'auction.tab.bidders': '入札者登録',
  'auction.tab.preview': '下見予約',
  'auction.tab.bids': '入札',
  'auction.tab.ranking': 'ランキング',
  'auction.tab.awards': '落札確定',
  'auction.round.title': '入札ウィンドウ',
  'auction.round.opensAt': '入札開始',
  'auction.round.cutoffAt': '入札締切',
  'auction.round.status': '状態',
  'auction.round.closedAt': '締切時刻',
  'auction.round.open': '開札',
  'auction.round.opening': '開札中',
  'auction.round.close': '締切',
  'auction.round.closing': '締切中',
  'auction.round.openSuccess': '開札しました',
  'auction.round.openError': '開札に失敗しました',
  'auction.round.closeSuccess': '締切しました',
  'auction.round.closeError': '締切に失敗しました',
  'auction.round.none': 'まだ開札していません。',
  'auction.round.current': '現在のウィンドウ',
  'auction.bidders.title': '入札者一覧',
  'auction.bidders.orgId': '入札者組織 ID',
  'auction.bidders.invitedAt': '招待時刻',
  'auction.bidders.empty': '招待された入札者はいません。',
  'auction.bidders.register': '入札者を招待',
  'auction.bidders.registering': '招待中',
  'auction.bidders.orgPlaceholder': '入札者組織 ID',
  'auction.bidders.registerSuccess': '入札者を招待しました',
  'auction.bidders.registerError': '入札者の招待に失敗しました',
  'auction.bidders.hint': '招待（審査通過）されていない組織は入札・下見予約できません。',
  'auction.preview.title': '下見枠',
  'auction.preview.startAt': '開始',
  'auction.preview.endAt': '終了',
  'auction.preview.capacity': '定員',
  'auction.preview.status': '状態',
  'auction.preview.create': '下見枠を追加',
  'auction.preview.creating': '作成中',
  'auction.preview.createSuccess': '下見枠を作成しました',
  'auction.preview.createError': '下見枠の作成に失敗しました',
  'auction.preview.empty': '下見枠はありません。',
  'auction.preview.book': '予約',
  'auction.preview.booking': '予約中',
  'auction.preview.bookSuccess': '下見を予約しました',
  'auction.preview.bookError': '下見予約に失敗しました',
  'auction.preview.capacityHint': '定員は既定 1。超過予約はできません。',
  'auction.bids.title': '入札',
  'auction.bids.item': '品目',
  'auction.bids.unitPrice': '入札単価',
  'auction.bids.unitPricePlaceholder': '単価（円）',
  'auction.bids.submit': '入札する',
  'auction.bids.submitting': '送信中',
  'auction.bids.submitSuccess': '入札しました',
  'auction.bids.submitError': '入札に失敗しました',
  'auction.bids.closedHint': '締切後は入札できません。',
  'auction.bids.selectItem': '品目を選択してください',
  'auction.ranking.title': 'ランキング（品目別）',
  'auction.ranking.selectItem': '品目を選択してランキングを表示',
  'auction.ranking.rank': '順位',
  'auction.ranking.bidder': '入札者組織',
  'auction.ranking.unitPrice': '入札単価',
  'auction.ranking.submittedAt': '提出時刻',
  'auction.ranking.empty': 'この品目に有効な入札はありません。',
  'auction.ranking.tieHint': '並び順：単価の高い順。同額は先に提出した方を優先。',
  'auction.awards.title': '落札確定',
  'auction.awards.item': '品目',
  'auction.awards.winningBid': '落札する入札',
  'auction.awards.confirm': '落札確定',
  'auction.awards.confirming': '確定中',
  'auction.awards.confirmSuccess': '落札を確定しました',
  'auction.awards.confirmError': '落札確定に失敗しました',
  'auction.awards.confirmedTitle': '確定済み落札一覧',
  'auction.awards.empty': '確定済みの落札はありません。',
  'auction.awards.unitPrice': '落札単価',
  'auction.awards.awardee': '落札業者組織',
  'auction.awards.confirmedAt': '確定時刻',
  'auction.awards.feedsP2': '落札単価は二回目精算（P2）の三者分配の基準になります。',
  'auction.awards.closeFirst': '落札確定の前に入札ウィンドウを締切ってください。',

  // ---- メンバー決策 -------------------------------------------------------
  'adjust.title': 'メンバー決定',
  'adjust.subtitle': '落札単価が希望/最低単価を下回る場合、メンバー代表が品目ごとに希望調整・返品・預りから選択します。',
  'adjust.gateHint': '落札単価 ≥ 希望/最低単価の品目は決定不要。返品/預りは記録のみで精算しません（Q5/Q6 未承認）。',
  'adjust.item': '品目',
  'adjust.minPrice': '希望/最低単価',
  'adjust.auctionPrice': '落札単価',
  'adjust.needsDecision': '要決定',
  'adjust.noNeed': '決定不要',
  'adjust.decisionType': '決定種別',
  'adjust.type.priceAdjust': '希望調整',
  'adjust.type.return': '返品',
  'adjust.type.hold': '預り',
  'adjust.adjustedPrice': 'メンバー調整後単価',
  'adjust.adjustedPriceHint': 'J − M は 5〜20 円の範囲（J=落札単価, M=調整後単価）。',
  'adjust.gap': 'J − M 差額',
  'adjust.reason': '理由',
  'adjust.holdLocation': '預り場所',
  'adjust.holdDeadline': '預り期限',
  'adjust.reasonPlaceholder': '理由を入力（任意）',
  'adjust.holdLocationPlaceholder': '預り場所',
  'adjust.holdDeadlinePlaceholder': '預り期限（例 2026-07-31）',
  'adjust.submit': '決定を記録',
  'adjust.submitting': '記録中',
  'adjust.success': 'メンバー決定を記録しました',
  'adjust.error': '決定の記録に失敗しました',
  'adjust.cancel': '取消',
  'adjust.cancelling': '取消中',
  'adjust.cancelSuccess': '決定を取消しました',
  'adjust.cancelError': '決定の取消に失敗しました',
  'adjust.errGapRange': 'J − M は 5〜20 円の範囲です',
  'adjust.errAdjustedRequired': '希望調整には調整後単価が必要です',
  'adjust.form.title': '決定を追加',
  'adjust.list.title': '記録済み決定',
  'adjust.list.empty': 'メンバー決定はまだありません。',
  'adjust.status': '状態',
  'adjust.decidedAt': '決定時刻',
  'adjust.selectItem': '品目を選択',

  // ---- 三者分配 -----------------------------------------------------------
  'shares.title': '三者分配',
  'shares.subtitle': 'K = 落札単価 − メンバー調整後単価 を基準に、EF 20%／メンバー 40%／会場 40%、かつ P + Q + R = K。',
  'shares.ratioHint': '既定比率：EF 20%、メンバー 40%、会場 40%（整数単価、端数調整で等式を保持）。',
  'shares.item': '品目',
  'shares.j': '落札単価',
  'shares.m': 'メンバー調整後単価',
  'shares.k': 'K（差額）',
  'shares.p': 'P（EF 20%）',
  'shares.q': 'Q（メンバー 40%）',
  'shares.r': 'R（会場 40%）',
  'shares.sum': 'P + Q + R',
  'shares.check': '等式チェック',
  'shares.ok': '一致',
  'shares.mismatch': '不一致',
  'shares.mismatchHint': 'P + Q + R ≠ K — 数字系の報連相で確認後に二回目精算へ進めます。',
  'shares.source': '分配値は最新の P2 run から取得。run が無い場合は既定 20/40/40 で試算表示します。',
  'shares.empty': '分配対象の品目がありません（先に落札確定とメンバー決定が必要）。',
  'shares.preview': 'プレビュー（既定比率）',
  'shares.fromRun': 'P2 run より',
  'shares.loadError': '三者分配データの読み込みに失敗しました',

  // ---- 二回目精算 ---------------------------------------------------------
  'p2.title': '二回目精算',
  'p2.subtitle': '品目ごとに二回目（会場荷受け）重量を入力し、三者分配を適用して二回目総額を試算。T38 EF 実際請求金額は手入力。',
  'p2.items.title': '二回目試算品目',
  'p2.items.empty': '試算対象の品目がありません（先に落札確定とメンバー決定が必要）。',
  'p2.field.weight': '二回目重量（kg）',
  'p2.field.j': '落札単価',
  'p2.field.m': 'メンバー調整後単価',
  'p2.field.k': 'K',
  'p2.field.p': 'P（EF）',
  'p2.field.q': 'Q（メンバー）',
  'p2.field.r': 'R（会場）',
  'p2.shareHint': 'P/Q/R 未入力時は後端が既定 20/40/40 で分配。入力時は三者すべて入力し P+Q+R=K であること。',
  'p2.calc.button': '試算',
  'p2.calc.running': '試算中',
  'p2.calc.success': '二回目試算が完了しました',
  'p2.calc.error': '二回目試算に失敗しました',
  'p2.result.title': '二回目試算結果',
  'p2.result.total': '二回目試算総額',
  'p2.result.estimate': '二回目請求概算',
  'p2.result.itemsTitle': '品目明細',
  'p2.result.sUnit': '二回目単価',
  'p2.result.tSubtotal': '品目小計',
  'p2.manual.label': 'EF 実際請求金額（手入力）',
  'p2.manual.hint': '最新の試算 run に EF 実際請求金額（T38）を手入力します。',
  'p2.manual.placeholder': '金額を入力',
  'p2.manual.submit': '保存',
  'p2.manual.saving': '保存中',
  'p2.manual.saved': '保存済み',
  'p2.manual.success': 'EF 実際請求金額を保存しました',
  'p2.issue.cardTitle': '発行 → 入金確認',
  'p2.issue.currentState': '現在の状態',
  'p2.issue.label': '二回目 発行',
  'p2.issue.button': '発行',
  'p2.issue.running': '発行中…',
  'p2.issue.hint': '発行後、「入金確認」へ進みます。',
  'p2.issue.needT38': '発行には試算と T38（EF 実際請求金額）の入力が必要です。',
  'p2.issue.notReady': 'まだ発行できる状態ではありません（試算と T38 が必要）。',
  'p2.issue.success': '二回目精算を発行しました。案件は「二回目発行済」に進みました。',
  'p2.issue.error': '発行に失敗しました',
  'p2.payment.label': '二回目 入金確認',
  'p2.payment.button': '入金確認',
  'p2.payment.running': '確認中…',
  'p2.payment.notReady': '入金確認には先に発行が必要です。',
  'p2.payment.success': '入金を確認しました。案件は「二回目入金確認済」に進みました。',
  'p2.payment.error': '入金確認に失敗しました',
  'p2.manual.error': '保存に失敗しました',
  'p2.history.title': '試算履歴',
  'p2.history.run': 'Run',
  'p2.latest.title': '現在の最新 run',
  'p2.latest.none': 'P2 run はまだありません。',
  // ---- S7 最終重量（final-weight）----
  'finalWeight.title': '最終重量',
  'finalWeight.subtitle': 'アサヒ出荷重量・落札業者入荷重量・最終採用重量を段階ごとに表示し、差異を計算します。',
  'finalWeight.state.title': '状態と受領の推進',
  'finalWeight.state.current': '現在の状態',
  'finalWeight.state.lockedHint': '案件はクローズ・ロック済みです。最終重量は読み取り専用で、これ以上推進できません。',
  'finalWeight.state.notReady': 'まだ最終重量を受領できる段階ではありません（先に二回目入金確認が必要）。',
  'finalWeight.receive.button': '最終重量の受領を確認',
  'finalWeight.receive.running': '受領中',
  'finalWeight.receive.hint': '確認すると最終報告段階（FinalWeightReceived）へ進みます。',
  'finalWeight.receive.blockedByVariance': '最終重量の差異が 20kg を超えています。先に重量監査で現場確認を完了してください。',
  'finalWeight.receive.success': '最終重量の受領を確認しました',
  'finalWeight.receive.error': '最終重量の受領に失敗しました',
  'finalWeight.compare.title': '段階別 重量対照',
  'finalWeight.compare.empty': '入荷重量データがありません。',
  'finalWeight.compare.unavailable': '出荷／入荷重量データを取得できません。',
  'finalWeight.col.memberWeight': 'メンバー申告重量',
  'finalWeight.col.venueWeight': 'アサヒ出荷／会場重量',
  'finalWeight.col.finalWeight': '最終採用重量',
  'finalWeight.col.variance': '差異（kg）',
  'finalWeight.col.status': '状態',
  'finalWeight.status.ok': '原則として会場基準を採用',
  'finalWeight.status.overThreshold': '差異 > 20kg',
  'finalWeight.overThresholdHint': '最終重量と前段の差異は約 {kg}kg で 20kg を超えています。陳／工藤による現場報連相の確認が必要です。フロントエンドは権威重量を自動裁定しません。',
  'finalWeight.snapshot.none': '独立した最終重量スナップショットはありません（会場入荷重量を採用値とします）。',
  'finalWeight.reviews.title': '関連する重量差異レビュー',
  'finalWeight.reviews.empty': '重量差異レビューはありません。',
  // ---- S7 三回目精算 P3（設計上 無効）----
  'p3.title': '三回目精算',
  'p3.subtitle': 'この機能は設計上無効です。オーナーによる W41/W40 公式の承認後に有効化されます。',
  'p3.blocked.title': '三回目精算は未開放です',
  'p3.blocked.body': '三回目精算（W41/W40）の公式はオーナー承認待ちのため、規定によりこのページは無効です。システムは三回目計算を一切行いません。',
  'p3.blocked.q2': '対応する未解決 BLOCKER：Q2（三回目精算公式、エンジニアリング仕様書 §6.5）。',
  'p3.formula.title': '公式プレースホルダ（読み取り専用）',
  'p3.formula.hint': '以下は確認待ちの公式項目で、対照用です。実際の係数は入っていません。',
  'p3.formula.cell': '項目',
  'p3.formula.purpose': '用途',
  'p3.formula.value': '値',
  'p3.formula.pending': 'オーナー確認待ち',
  'p3.formula.w41': 'W41 三回目最終精算額',
  'p3.formula.w41Desc': '三回目の最終精算総額（公式は確認待ち）。',
  'p3.formula.w40': 'W40 三回目差異調整',
  'p3.formula.w40Desc': '最終重量差異による精算額の調整（公式は確認待ち）。',
  'p3.calc.button': '試算',
  'p3.calc.disabledHint': '試算は無効です：オーナーによる三回目公式の確認後に開放されます。',
  'p3.probe.title': 'バックエンドのガードを検証',
  'p3.probe.hint': '三回目精算 API を呼び出し、未確認公式に対するバックエンドのブロックを確認します（blocked が返る想定で、計算結果は一切表示しません）。',
  'p3.probe.button': 'API が blocked を返すか検証',
  'p3.probe.running': '検証中',
  'p3.probe.done': 'バックエンドが応答し、三回目がまだブロックされていることを確認しました。',
  'p3.probe.confirmedBlocked': '確認済み：バックエンドが三回目精算をブロックしています（公式未確認）。',
  'p3.probe.blockedGeneric': 'バックエンドがブロックを返しました（想定外のエラーコード）',
  'p3.probe.unexpected': '異常：三回目が成功を返しました。直ちに停止して確認してください（発生してはなりません）。',
  // ---- S8 最終報告 + クローズ（final-report）----
  'finalReport.title': '最終報告 / クローズ',
  'finalReport.subtitle': '報告スナップショットと三者確認を表示し、確認後にクローズして Closed_Locked へ進みます。',
  'finalReport.locked.badge': 'ロック済み',
  'finalReport.locked.title': '案件はクローズ・ロックされました',
  'finalReport.locked.body': '案件は Closed_Locked に入り、業務データは 100% 変更できません。このページは読み取り専用です。',
  'finalReport.snapshot.title': '報告スナップショット',
  'finalReport.snapshot.subject': '案件名',
  'finalReport.snapshot.state': '現在の状態',
  'finalReport.snapshot.none': '独立した最終報告スナップショットはありません（案件マスタと状態で表示）。',
  'finalReport.snapshot.hash': '報告ハッシュ（blake3）',
  'finalReport.confirm.title': '三者確認',
  'finalReport.confirm.hint': 'クローズ前に EF・億成（メンバー）・アサヒ（会場）の三者が最終報告を確認します。',
  'finalReport.confirm.confirmed': '確認済み',
  'finalReport.confirm.pending': '確認待ち',
  'finalReport.confirm.button': '確認',
  'finalReport.confirm.success': '確認を記録しました',
  'finalReport.confirm.error': '確認に失敗しました',
  'finalReport.confirm.allConfirmed': '三者すべて確認済み。クローズできます。',
  'finalReport.confirm.progress': '三者確認の進捗：{n}/{total}。',
  'finalReport.role.ef': 'EF（仲介）',
  'finalReport.role.yicheng': '億成（メンバー）',
  'finalReport.role.asahi': 'アサヒ（会場）',
  'finalReport.precondition.title': 'クローズ前提条件',
  'finalReport.precondition.none': '重量差異レビューはなく、クローズ前提条件を満たしています。',
  'finalReport.precondition.allResolved': 'すべての重量差異レビューが解決済みで、クローズ前提条件を満たしています。',
  'finalReport.precondition.blocked': '未解決の重量差異レビューが {n} 件あります。現場確認を完了しないとクローズできません。',
  'finalReport.actions.title': '推進アクション',
  'finalReport.review.label': '最終報告レビューを開始',
  'finalReport.review.button': '最終報告レビューを開始',
  'finalReport.review.running': '処理中',
  'finalReport.review.notReady': 'まだレビューを開始できる段階ではありません（先に最終重量の受領が必要）。',
  'finalReport.review.success': '最終報告レビューを開始しました',
  'finalReport.review.error': '最終報告レビューの開始に失敗しました',
  'finalReport.close.label': 'クローズ・ロック',
  'finalReport.close.button': 'クローズしてロック（Closed_Locked）',
  'finalReport.close.running': 'クローズ中',
  'finalReport.close.hint': 'クローズ後、案件は Closed_Locked に入り、業務データは変更できなくなります。',
  'finalReport.close.notReady': 'まだクローズできる段階ではありません（先に最終報告レビューの開始が必要）。',
  'finalReport.close.blockedByVariance': '未解決の重量差異レビューがあるため、クローズできません。',
  'finalReport.close.blockedByConfirm': '三者確認が揃っていないため、クローズできません。',
  'finalReport.close.success': '案件をクローズ・ロックしました',
  'finalReport.close.error': 'クローズに失敗しました',

  // ---- 2FA / TOTP ----
  'nav.security': 'アカウントセキュリティ',
  'desc.security': '二要素認証（2FA / TOTP）を管理。高リスク操作には step-up 認証が必要です。',
  'twofa.title': '二要素認証（2FA）',
  'twofa.subtitle': '有効化すると、精算の手入力・クローズ・落札確定・重量裁定などの高リスク操作でワンタイムコードの再入力が必要になります。',
  'twofa.status': '現在の状態',
  'twofa.enabled': '有効',
  'twofa.disabled': '無効',
  'twofa.codePlaceholder': '6 桁コード',
  'twofa.setup.intro': 'Google Authenticator / Authy などのアプリでスキャン、または手動でシークレットを入力して 2FA を有効化します。',
  'twofa.setup.start': '2FA 設定を開始',
  'twofa.setup.starting': '生成中…',
  'twofa.setup.ready': 'シークレットを生成しました。アプリでスキャンしてコードを入力してください。',
  'twofa.setup.error': '2FA シークレットの生成に失敗しました',
  'twofa.setup.scan': 'QR をスキャン、またはシークレットを手動入力',
  'twofa.setup.scanHint': '以下の otpauth URI を authenticator アプリに追加してください（多くのアプリは URI 貼り付けまたは手動入力に対応）。',
  'twofa.setup.secret': 'シークレット',
  'twofa.verify.label': 'アプリに表示される 6 桁コードを入力',
  'twofa.verify.submit': '検証して有効化',
  'twofa.verify.submitting': '検証中…',
  'twofa.verify.success': '2FA を有効化しました',
  'twofa.verify.error': 'コードが正しくないか期限切れです',
  'twofa.disable.intro': '2FA を無効化するには有効なコードの入力が必要です。無効化後は高リスク操作で step-up を求められなくなります。',
  'twofa.disable.label': '6 桁コードを入力して無効化',
  'twofa.disable.submit': '2FA を無効化',
  'twofa.disable.submitting': '無効化中…',
  'twofa.disable.success': '2FA を無効化しました',
  'twofa.disable.error': '無効化に失敗しました（コード誤り、または未有効）',
  'stepUp.title': '二要素認証が必要',
  'stepUp.desc': '高リスク操作です。続行するには authenticator アプリのワンタイムコードを入力してください。',
  'stepUp.codeLabel': 'ワンタイムコード',
  'stepUp.codePlaceholder': '6 桁コード',
  'stepUp.hint': 'まだ 2FA を有効化していない場合は「アカウントセキュリティ」で有効化してください。',
  'stepUp.confirm': '確認して送信',
  'iam.error.step_up_required': 'この操作には 2FA の有効化とワンタイムコードが必要です。',
  'iam.error.step_up_invalid': '二要素認証コードが正しくないか期限切れです。',
  'iam.totp.enabled': '2FA を有効化しました。',
  'iam.totp.disabled': '2FA を無効化しました。',
  // ---- 報連相（hourensou，spec §10 / §D5）----
  'hourensou.title': '報・連・相',
  'hourensou.subtitle': '20kg 重量差異、三方配分 P+Q+R≠K、工程遅延など、現場/数値/工程の確認が必要な事項をここで追跡・解決します。',
  'hourensou.empty': '報連相はまだありません。',
  'hourensou.loadError': '報連相一覧を取得できませんでした',
  'hourensou.col.type': '区分',
  'hourensou.col.category': '事項',
  'hourensou.col.assignee': '担当',
  'hourensou.col.message': '内容',
  'hourensou.col.status': '状態',
  'hourensou.col.action': '操作',
  'hourensou.type.field': '現場系',
  'hourensou.type.numeric': '数値系',
  'hourensou.type.process': '工程系',
  'hourensou.status.open': '未対応',
  'hourensou.status.acknowledged': '確認中',
  'hourensou.status.resolved': '解決済',
  'hourensou.resolve.button': '解決済にする',
  'hourensou.resolve.notePlaceholder': '解決メモ（任意）',
  'hourensou.resolve.success': '報連相を解決済にしました',
  'hourensou.resolve.error': '報連相の解決に失敗しました',

  // ---- Freee SLA + 財務書類参照（spec §10.3 / §B4；Freee API は呼ばない）----
  'freee.title': 'Freee SLA / 財務書類',
  'freee.subtitle': '請求書・仕切書は精算確定から 3 営業日以内に発行（Freee API は呼ばず、計時と通知のみ）。',
  'freee.sla.heading': 'Freee SLA タイマー',
  'freee.sla.empty': 'Freee SLA タイマーはまだありません。',
  'freee.sla.loadError': 'Freee SLA 一覧を取得できませんでした',
  'freee.sla.col.phase': '精算フェーズ',
  'freee.sla.col.type': '書類',
  'freee.sla.col.status': '状態',
  'freee.sla.col.issuedAt': '発行起点',
  'freee.sla.col.dueAt': '期限（+3 営業日）',
  'freee.sla.col.action': '操作',
  'freee.sla.type.invoice': '請求書',
  'freee.sla.type.receipt': '仕切書 / 検収書',
  'freee.sla.status.pending': '計時中',
  'freee.sla.status.warning': '期限間近',
  'freee.sla.status.overdue': '期限超過',
  'freee.sla.status.completed': '完了',
  'freee.sla.complete.button': '発行済にする',
  'freee.sla.complete.success': 'Freee task を完了にしました',
  'freee.sla.complete.error': 'Freee task の完了に失敗しました',
  'freee.doc.heading': '財務書類参照',
  'freee.doc.empty': '財務書類参照はまだありません。',
  'freee.doc.loadError': '財務書類一覧を取得できませんでした',
  'freee.doc.col.type': '種別',
  'freee.doc.col.freeeNo': 'freee 管理番号',
  'freee.doc.col.invoiceNo': '請求書番号',
  'freee.doc.col.statementNo': '仕切書番号',
  'freee.doc.col.note': '備考',
  'freee.doc.form.heading': '財務書類参照を記録',
  'freee.doc.form.docType': '種別',
  'freee.doc.form.freeeNo': 'freee 管理番号',
  'freee.doc.form.invoiceNo': '請求書番号',
  'freee.doc.form.statementNo': '仕切書番号',
  'freee.doc.form.attachment': '添付参照',
  'freee.doc.form.note': '備考',
  'freee.doc.form.submit': '記録',
  'freee.doc.form.success': '財務書類参照を記録しました',
  'freee.doc.form.error': '財務書類参照の記録に失敗しました',
  'freee.rbac.forbidden': 'このセクションの閲覧 / 操作権限がありません。',
}

const en: Dictionary = {
  ...zhTW,
  // --- Case item details (/cases/:id/items) ---
  'case.items.title': 'Item details',
  'case.items.subtitle':
    'Maintain item name, estimated weight, min/target unit price and packaging; edit per item (price versions are recorded by the backend).',
  'case.items.editTitle': 'Edit item',
  'case.items.editAction': 'Edit',
  'case.items.lockedHint': 'Case is closed and locked (Closed_Locked); items cannot be modified.',
  'case.items.loadError': 'Unable to load items',
  'case.items.success': 'Item updated',
  'case.items.error': 'Failed to update item',
  'case.items.empty': 'This case has no items yet.',
  'case.items.reasonHint': 'Reason for change (recorded in the price version; optional).',
  'common.save': 'Save',
  'field.targetUnitPrice': 'Target unit price',
  'field.packageForm': 'Packaging',
  'field.reason': 'Reason',
  'app.name': 'ECO FAITH Auction Collaboration',
  'auth.email': 'Account',
  'auth.password': 'Password',
  'auth.signIn': 'Sign in',
  'auth.signOut': 'Sign out',
  'nav.dashboard': 'Dashboard',
  'nav.masterData': 'Master Data',
  'nav.partners': 'Partners',
  'nav.cases': 'Cases',
  'nav.receiving': 'Receiving',
  'nav.inventory': 'Inventory',
  'nav.sales': 'Bidding',
  'nav.settlements': 'Settlements',
  'nav.admin': 'System Admin',
  'nav.adminUsers': 'Users',
  'nav.adminRoles': 'Roles & Permissions',
  'nav.reports': 'Reports',
  'nav.audit': 'Audit',
  'nav.notifications': 'Notifications',
  // ---- Reports hub (/reports) ---------------------------------------------
  'reports.title': 'Reports',
  'reports.subtitle':
    'Cross-case progress, weight variance, settlements, auctions, Freee SLA and audit — query and export.',
  'reports.tab.casesProgress': 'Cases progress',
  'reports.tab.weight': 'Weight variance',
  'reports.tab.settlements': 'Settlements',
  'reports.tab.auctions': 'Auctions',
  'reports.tab.freeeSla': 'Freee SLA',
  'reports.tab.audit': 'Audit',
  'reports.filter.from': 'From',
  'reports.filter.to': 'To',
  'reports.filter.status': 'Status',
  'reports.filter.statusAll': 'All statuses',
  'reports.filter.statusPlaceholder': 'Status (exact match)',
  'reports.filter.caseNo': 'Case no.',
  'reports.filter.caseNoPlaceholder': 'Case no. (exact match)',
  'reports.filter.apply': 'Apply',
  'reports.filter.reset': 'Reset',
  'reports.exportCsv': 'Export CSV',
  'reports.export.empty': 'No rows to export',
  'reports.loadError': 'Failed to load report',
  'reports.empty': 'No data for this filter',
  'reports.total': '{total} rows',
  'reports.prev': 'Prev',
  'reports.next': 'Next',
  'reports.page': 'Page {page}',
  'reports.col.caseNo': 'Case no.',
  'reports.col.title': 'Title',
  'reports.col.workflowState': 'Workflow state',
  'reports.col.memberOrg': 'Member org',
  'reports.col.venueOrg': 'Venue org',
  'reports.col.openExceptions': 'Open exceptions',
  'reports.col.nextStep': 'Next step',
  'reports.col.createdAt': 'Created at',
  'reports.col.reviewId': 'Review ID',
  'reports.col.caseId': 'Case ID',
  'reports.col.itemName': 'Item',
  'reports.col.varianceType': 'Variance type',
  'reports.col.memberWeight': 'Member weight (kg)',
  'reports.col.venueWeight': 'Venue weight (kg)',
  'reports.col.finalWeight': 'Final weight (kg)',
  'reports.col.varianceKg': 'Variance (kg)',
  'reports.col.thresholdKg': 'Threshold (kg)',
  'reports.col.status': 'Status',
  'reports.col.p1IssuedTotal': 'P1 calc total (incl. tax)',
  'reports.col.p1Reference50': 'P1 50% reference advance',
  'reports.col.p1ActualRequest': 'P1 EF actual request',
  'reports.col.p1Status': 'P1 status',
  'reports.col.p2PreTaxTotal': 'P2 calc total (excl. tax)',
  'reports.col.p2ProfitShare': 'P2 profit share',
  'reports.col.p2ActualRequest': 'P2 EF actual request',
  'reports.col.p2Status': 'P2 status',
  'reports.col.p3Status': 'P3 status',
  'reports.col.settlementNone': '—',
  'reports.col.biddersCount': 'Bidders',
  'reports.col.bidsCount': 'Bids',
  'reports.col.awardsCount': 'Awards',
  'reports.col.awardedUnitPrice': 'Awarded unit price',
  'reports.col.awardedConfirmedAt': 'Awarded at',
  'reports.col.taskId': 'Task ID',
  'reports.col.settlementPhase': 'Phase',
  'reports.col.taskType': 'Doc type',
  'reports.col.issuedAt': 'Issued at',
  'reports.col.warningAt': 'Warning at',
  'reports.col.dueAt': 'Due at',
  'reports.col.completedAt': 'Completed at',
  'reports.col.auditId': 'ID',
  'reports.col.actorUser': 'Actor user',
  'reports.col.organization': 'Org ID',
  'reports.col.eventName': 'Event',
  'reports.col.resourceType': 'Resource type',
  'reports.col.resourceId': 'Resource ID',
  'reports.col.beforeSummary': 'Before summary',
  'reports.col.afterSummary': 'After summary',
  'desc.adminRoles': 'Manage role / permission bindings independent of case flow.',
  'common.underConstruction': 'Under construction',
  'common.underConstructionDesc':
    'This screen is being built; the route and navigation are ready, features land soon.',
  'case.new': 'New case',
  'rbac.forbidden': 'No permission',
  'rbac.forbiddenTitle': 'Cannot access this page',
  'rbac.forbiddenDesc': 'Your role is not authorized for this feature. Contact an administrator.',
  'rbac.disabledTooltip': 'This feature is not yet enabled; awaiting owner sign-off.',
  'common.create': 'Create',
  'common.update': 'Save',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.cancel': 'Cancel',
  'common.refresh': 'Refresh',
  'common.loading': 'Loading',
  'common.error': 'Unable to load data',
  'case.workspace': 'Case Workspace',
  'case.list.title': 'Cases',
  'case.list.subtitle': 'Search and browse all auction-collaboration cases. Click a row to open its workspace.',
  'case.list.itemCount': 'Items',
  'case.list.createdAt': 'Created',
  'case.list.searchSeller': 'Search seller',
  'case.list.searchCaseNo': 'Search case no.',
  'case.list.prev': 'Previous',
  'case.list.next': 'Next',
  'case.list.empty': 'No cases yet. Use “New case” to create the first one.',
  'case.list.loadError': 'Failed to load cases',
  'case.new.title': 'New case',
  'case.new.subtitle': 'Enter seller and item lines, then submit. Weights/prices are sent as strings to avoid float error.',
  'case.new.headerSection': 'Case header',
  'case.new.itemsSection': 'Item lines',
  'case.new.addItem': 'Add item',
  'case.new.removeItem': 'Remove',
  'case.new.submit': 'Create case',
  'case.new.submitting': 'Creating…',
  'case.new.success': 'Case created',
  'case.new.error': 'Failed to create case',
  'case.new.loadSample': 'Load EF260128 sample',
  'case.new.itemsRequired': 'At least one item is required',
  'case.detail.title': 'Case overview',
  'case.detail.loadError': 'Failed to load case',
  'case.detail.headerCard': 'Case info',
  'case.detail.itemsCard': 'Item lines',
  'field.itemName': 'Item name',
  'field.declaredWeight': 'Weight kg',
  'field.estimatedWeight': 'Est. weight kg',
  'field.desiredUnitPrice': 'Desired/min price',
  'field.minUnitPrice': 'Min unit price',
  'field.quantityCount': 'Quantity',
  'field.uom': 'Unit',
  'field.bagUnitWeight': 'Bag wt kg',
  'field.listingNotes': 'Listing notes',
  'field.itemNotes': 'Item notes',
  'field.caseTitle': 'Case title',
  'field.workflowState': 'Workflow state',
  'field.currencyCode': 'Currency',
  'option.packFlexible': 'Flexible bag',
  'option.packSmallFlexible': 'Small flexible',
  'option.packBara': 'Loose (bara)',
  'option.packPallet': 'Pallet',
  'option.packOther': 'Other',
  // S4 P1 first settlement
  'case.new.titlePlaceholder': 'Optional; case no. is auto-generated',
  'case.new.memberOrgLabel': 'Member org (optional)',
  'case.new.memberOrgPlaceholder': 'Default: own organization',
  'case.detail.goP1': 'Go to first settlement',
  'case.detail.venue': 'Venue',
  'case.detail.venueUnset': 'Not set',
  'case.lifecycle.title': 'Case lifecycle actions',
  'case.venue.label': 'Set venue',
  'case.venue.select': 'Select venue organization',
  'case.venue.placeholder': 'Choose a venue',
  'case.venue.confirm': 'Confirm venue',
  'case.venue.saving': 'Saving…',
  'case.venue.success': 'Venue set; case advanced to Detail Accepted.',
  'case.venue.error': 'Failed to set venue',
  'case.venue.alreadySet': 'Venue already set (org #{id}).',
  'case.ship.label': 'Confirm shipment',
  'case.ship.confirm': 'Confirm shipment',
  'case.ship.shipping': 'Processing…',
  'case.ship.notReady': 'Set the venue and accept details before shipping.',
  'case.ship.hint': 'After shipping, proceed to truck / item weight audit.',
  'case.ship.success': 'Shipment confirmed; case advanced to Shipped.',
  'case.ship.error': 'Failed to confirm shipment',
  'p1.title': 'First settlement (P1)',
  'p1.subtitle':
    'Prefilled from case items (weight, min unit price) to estimate the advance. Each calculation creates a new settlement run without overwriting prior results.',
  'p1.items.title': 'Settlement items',
  'p1.items.empty': 'This case has no items. Create items in the case overview first.',
  'p1.field.grossWeight': 'Gross weight (with bag) kg',
  'p1.calc.button': 'Calculate',
  'p1.calc.running': 'Calculating…',
  'p1.calc.success': 'P1 calculated; a new run was created',
  'p1.calc.error': 'P1 calculation failed',
  'p1.result.title': 'Calculation result',
  'p1.result.preTaxSum': 'Pre-tax subtotal',
  'p1.result.inclTaxTotal': 'First-settlement total (incl. tax)',
  'p1.result.reference50': '50% reference advance',
  'p1.manual.label': 'EF actual billed amount',
  'p1.manual.hint': 'EF enters the actual billed amount based on the estimate; saved against this run.',
  'p1.manual.placeholder': 'Enter amount (number)',
  'p1.manual.submit': 'Submit',
  'p1.manual.saving': 'Saving…',
  'p1.manual.saved': 'Saved',
  'p1.manual.success': 'EF actual billed amount recorded',
  'p1.manual.error': 'Failed to record EF actual billed amount',
  'p1.history.title': 'Calculation run history',
  'p1.history.run': 'Run',
  // S3 shipment
  'shipment.current.title': 'Shipment status',
  'shipment.current.empty': 'No shipment plan created yet.',
  'shipment.current.loadError': 'Failed to load shipment',
  'shipment.plan.title': 'Create / update shipment plan',
  'shipment.plan.submit': 'Submit shipment plan',
  'shipment.plan.submitting': 'Submitting…',
  'shipment.plan.success': 'Shipment plan saved',
  'shipment.plan.error': 'Failed to save shipment plan',
  'shipment.field.shipmentNo': 'Shipment no.',
  'shipment.field.plannedShipAt': 'Planned ship time',
  'shipment.field.plannedArrivalAt': 'Planned arrival time',
  'shipment.field.logisticsCompany': 'Carrier',
  'shipment.field.truckType': 'Truck type',
  'shipment.field.driver': 'Driver',
  'shipment.field.driverPhone': 'Driver phone',
  'shipment.field.scaleRef': 'Scale ticket ref / notes',
  'shipment.field.scaleRefHint': 'Enter a scale-ticket number or file reference here; formal ticket upload is handled in the shipment-evidence step.',
  'shipment.field.truckInfoPending': 'Truck info pending',
  // S3 receiving
  'receiving.title': 'Receiving',
  'receiving.noShipment': 'No shipment plan yet. Create a shipment under “Shipment” before receiving.',
  'receiving.weight.title': 'Venue weighed total',
  'receiving.weight.submit': 'Record total weight',
  'receiving.weight.submitting': 'Recording…',
  'receiving.weight.success': 'Venue total weight recorded',
  'receiving.weight.error': 'Failed to record venue total weight',
  'receiving.details.title': 'Per-item receiving',
  'receiving.details.empty': 'No case items to confirm.',
  'receiving.details.submit': 'Record item confirmation',
  'receiving.details.submitting': 'Recording…',
  'receiving.details.success': 'Per-item receiving recorded',
  'receiving.details.error': 'Failed to record per-item receiving',
  'receiving.field.venueTotalWeight': 'Venue total kg',
  'receiving.field.measuredAt': 'Weighed at',
  'receiving.field.notes': 'Notes',
  'receiving.field.receivedItemName': 'Received item name',
  'receiving.field.receivedWeight': 'Received weight kg',
  'receiving.field.condition': 'Condition',
  'receiving.field.conditionNotes': 'Condition notes',
  'receiving.condition.asListed': 'As listed',
  'receiving.condition.minor': 'Minor issue',
  'receiving.condition.major': 'Major issue',
  // S3 weight audit (20kg rule)
  'weightAudit.compare.title': 'Weight comparison (member vs venue)',
  'weightAudit.compare.empty': 'No received items to compare. Complete receiving first.',
  'weightAudit.col.memberWeight': 'Member kg',
  'weightAudit.col.venueWeight': 'Venue kg',
  'weightAudit.col.variance': 'Variance kg',
  'weightAudit.col.threshold': 'Threshold kg',
  'weightAudit.col.varianceType': 'Variance type',
  'weightAudit.col.status': 'Status',
  'weightAudit.col.resolution': 'Resolution',
  'weightAudit.col.action': 'Action',
  'weightAudit.status.useVenue': 'Use venue baseline (default)',
  'weightAudit.status.overThreshold': 'Over 20kg, review needed',
  'weightAudit.overThresholdHint': 'Weight variance {kg}kg: a field 報連相 review must be created; please ask Chen/Kudo to confirm. The settlement-impacting next step is blocked until confirmed.',
  'weightAudit.create.button': 'Create variance review',
  'weightAudit.create.success': 'Variance review created',
  'weightAudit.create.error': 'Failed to create variance review',
  'weightAudit.reviews.title': 'Weight variance reviews',
  'weightAudit.reviews.empty': 'No weight variance reviews.',
  'weightAudit.state.title': 'Current workflow state',
  'weightAudit.state.current': 'State',
  'weightAudit.truck.title': 'Truck total weight (truck scale vs venue scale)',
  'weightAudit.truck.hint': 'Enter member truck total and venue scale total. Diff ≤ 20kg adopts the venue value and advances to Venue Received; > 20kg requires three-party confirmation.',
  'weightAudit.truck.memberTotal': 'Member truck total (kg)',
  'weightAudit.truck.venueTotal': 'Venue scale total (kg)',
  'weightAudit.truck.submit': 'Submit truck total',
  'weightAudit.truck.submitting': 'Submitting…',
  'weightAudit.truck.accepted': 'Truck total accepted at venue value; advanced to Venue Received.',
  'weightAudit.truck.blocked': 'Variance over 20kg; a weight variance review was created. Three-party confirmation required.',
  'weightAudit.truck.error': 'Failed to submit truck total',
  'weightAudit.item.title': 'Item weight table (member vs venue)',
  'weightAudit.item.hint': 'Enter member and venue weight per item and submit once. All diffs ≤ 20kg advances to Weight Tables Received; any > 20kg requires three-party confirmation.',
  'weightAudit.item.noItems': 'This case has no items.',
  'weightAudit.item.submit': 'Submit item weights',
  'weightAudit.item.submitting': 'Submitting…',
  'weightAudit.item.accepted': 'Item weight table received; advanced to Weight Tables Received.',
  'weightAudit.item.blocked': 'Some items exceed 20kg; a weight variance review was created. Three-party confirmation required.',
  'weightAudit.item.error': 'Failed to submit item weights',
  'weightAudit.resolve.useVenue': 'Use venue weight',
  'weightAudit.resolve.success': 'Resolution submitted',
  'weightAudit.resolve.error': 'Failed to submit resolution',

  // ---- Auction workspace ----
  'auction.title': 'Auction management',
  'auction.subtitle': 'Open round, review bidders, preview booking, bidding, ranking, award.',
  'auction.tab.round': 'Round / publish',
  'auction.tab.bidders': 'Bidders',
  'auction.tab.preview': 'Preview booking',
  'auction.tab.bids': 'Submit bid',
  'auction.tab.ranking': 'Ranking',
  'auction.tab.awards': 'Award',
  'auction.round.title': 'Bidding window',
  'auction.round.opensAt': 'Bid start',
  'auction.round.cutoffAt': 'Bid cutoff',
  'auction.round.status': 'Status',
  'auction.round.closedAt': 'Closed at',
  'auction.round.open': 'Open round',
  'auction.round.opening': 'Opening',
  'auction.round.close': 'Close round',
  'auction.round.closing': 'Closing',
  'auction.round.openSuccess': 'Round opened',
  'auction.round.openError': 'Failed to open round',
  'auction.round.closeSuccess': 'Round closed',
  'auction.round.closeError': 'Failed to close round',
  'auction.round.none': 'No round opened yet.',
  'auction.round.current': 'Current window',
  'auction.bidders.title': 'Bidders',
  'auction.bidders.orgId': 'Bidder org ID',
  'auction.bidders.invitedAt': 'Invited at',
  'auction.bidders.empty': 'No invited bidders yet.',
  'auction.bidders.register': 'Invite bidder',
  'auction.bidders.registering': 'Inviting',
  'auction.bidders.orgPlaceholder': 'Bidder org ID',
  'auction.bidders.registerSuccess': 'Bidder invited',
  'auction.bidders.registerError': 'Failed to invite bidder',
  'auction.bidders.hint': 'Un-invited (unapproved) orgs cannot bid or book a preview.',
  'auction.preview.title': 'Preview slots',
  'auction.preview.startAt': 'Start',
  'auction.preview.endAt': 'End',
  'auction.preview.capacity': 'Capacity',
  'auction.preview.status': 'Status',
  'auction.preview.create': 'Add slot',
  'auction.preview.creating': 'Creating',
  'auction.preview.createSuccess': 'Preview slot created',
  'auction.preview.createError': 'Failed to create preview slot',
  'auction.preview.empty': 'No preview slots yet.',
  'auction.preview.book': 'Book',
  'auction.preview.booking': 'Booking',
  'auction.preview.bookSuccess': 'Preview booked',
  'auction.preview.bookError': 'Failed to book preview',
  'auction.preview.capacityHint': 'Capacity defaults to 1; no overbooking.',
  'auction.bids.title': 'Submit bid',
  'auction.bids.item': 'Item',
  'auction.bids.unitPrice': 'Bid unit price',
  'auction.bids.unitPricePlaceholder': 'Unit price (JPY)',
  'auction.bids.submit': 'Submit bid',
  'auction.bids.submitting': 'Submitting',
  'auction.bids.submitSuccess': 'Bid submitted',
  'auction.bids.submitError': 'Failed to submit bid',
  'auction.bids.closedHint': 'Bidding is closed after the cutoff.',
  'auction.bids.selectItem': 'Please select an item',
  'auction.ranking.title': 'Ranking (per item)',
  'auction.ranking.selectItem': 'Select an item to view ranking',
  'auction.ranking.rank': 'Rank',
  'auction.ranking.bidder': 'Bidder org',
  'auction.ranking.unitPrice': 'Bid unit price',
  'auction.ranking.submittedAt': 'Submitted at',
  'auction.ranking.empty': 'No valid bids for this item.',
  'auction.ranking.tieHint': 'Order: price high→low; ties go to the earlier submission.',
  'auction.awards.title': 'Award confirmation',
  'auction.awards.item': 'Item',
  'auction.awards.winningBid': 'Winning bid',
  'auction.awards.confirm': 'Confirm award',
  'auction.awards.confirming': 'Confirming',
  'auction.awards.confirmSuccess': 'Award confirmed',
  'auction.awards.confirmError': 'Failed to confirm award',
  'auction.awards.confirmedTitle': 'Confirmed awards',
  'auction.awards.empty': 'No confirmed awards yet.',
  'auction.awards.unitPrice': 'Award unit price',
  'auction.awards.awardee': 'Awardee org',
  'auction.awards.confirmedAt': 'Confirmed at',
  'auction.awards.feedsP2': 'The award unit price feeds the P2 three-way split.',
  'auction.awards.closeFirst': 'Close the bidding window before confirming an award.',

  // ---- Member decision ----------------------------------------------------
  'adjust.title': 'Member decision',
  'adjust.subtitle':
    'When the award price is below the desired/minimum price, the member rep chooses per item: price adjust, return, or hold.',
  'adjust.gateHint':
    'Items with award price ≥ desired/minimum price need no decision. Return/hold are record-only (Q5/Q6 not signed off).',
  'adjust.item': 'Item',
  'adjust.minPrice': 'Desired / min price',
  'adjust.auctionPrice': 'Award price',
  'adjust.needsDecision': 'Needs decision',
  'adjust.noNeed': 'No decision',
  'adjust.decisionType': 'Decision type',
  'adjust.type.priceAdjust': 'Price adjust',
  'adjust.type.return': 'Return',
  'adjust.type.hold': 'Hold',
  'adjust.adjustedPrice': 'Adjusted member unit price',
  'adjust.adjustedPriceHint': 'J − M must be within 5–20 yen (J=award price, M=adjusted price).',
  'adjust.gap': 'J − M gap',
  'adjust.reason': 'Reason',
  'adjust.holdLocation': 'Hold location',
  'adjust.holdDeadline': 'Hold deadline',
  'adjust.reasonPlaceholder': 'Enter reason (optional)',
  'adjust.holdLocationPlaceholder': 'Hold location',
  'adjust.holdDeadlinePlaceholder': 'Hold deadline (e.g. 2026-07-31)',
  'adjust.submit': 'Record decision',
  'adjust.submitting': 'Recording',
  'adjust.success': 'Member decision recorded',
  'adjust.error': 'Failed to record decision',
  'adjust.cancel': 'Cancel',
  'adjust.cancelling': 'Cancelling',
  'adjust.cancelSuccess': 'Decision cancelled',
  'adjust.cancelError': 'Failed to cancel decision',
  'adjust.errGapRange': 'J − M must be within 5–20 yen',
  'adjust.errAdjustedRequired': 'Price adjust requires an adjusted unit price',
  'adjust.form.title': 'Add decision',
  'adjust.list.title': 'Recorded decisions',
  'adjust.list.empty': 'No member decisions yet.',
  'adjust.status': 'Status',
  'adjust.decidedAt': 'Decided at',
  'adjust.selectItem': 'Select item',

  // ---- Three-way split (P+Q+R=K) -----------------------------------------
  'shares.title': 'Three-way split',
  'shares.subtitle':
    'Based on K = award price − adjusted member price, with EF 20% / member 40% / venue 40%, and P + Q + R = K.',
  'shares.ratioHint':
    'Default ratios: EF 20%, member 40%, venue 40% (integer unit prices; remainder kept on R to preserve the equality).',
  'shares.item': 'Item',
  'shares.j': 'Award price',
  'shares.m': 'Adjusted member price',
  'shares.k': 'K (gap)',
  'shares.p': 'P (EF 20%)',
  'shares.q': 'Q (member 40%)',
  'shares.r': 'R (venue 40%)',
  'shares.sum': 'P + Q + R',
  'shares.check': 'Equality check',
  'shares.ok': 'Match',
  'shares.mismatch': 'Mismatch',
  'shares.mismatchHint':
    'P + Q + R ≠ K — requires a numeric ho-ren-so confirmation before proceeding to P2.',
  'shares.source':
    'Split values come from the latest P2 run; with no run, a default 20/40/40 preview is shown.',
  'shares.empty': 'No splittable items yet (award confirmation and member decisions required first).',
  'shares.preview': 'Preview (default ratios)',
  'shares.fromRun': 'From P2 run',
  'shares.loadError': 'Failed to load three-way split data',

  // ---- Second settlement (P2) --------------------------------------------
  'p2.title': 'Second settlement',
  'p2.subtitle':
    'Enter the second (venue-received) weight per item, apply the three-way split to estimate the second total; T38 EF actual invoice amount is manual.',
  'p2.items.title': 'Second settlement items',
  'p2.items.empty': 'No items to calculate yet (award confirmation and member decisions required first).',
  'p2.field.weight': 'Second weight (kg)',
  'p2.field.j': 'Award price',
  'p2.field.m': 'Adjusted member price',
  'p2.field.k': 'K',
  'p2.field.p': 'P (EF)',
  'p2.field.q': 'Q (member)',
  'p2.field.r': 'R (venue)',
  'p2.shareHint':
    'If P/Q/R are blank the backend applies the default 20/40/40 split; if provided, all three are required and P+Q+R=K.',
  'p2.calc.button': 'Calculate',
  'p2.calc.running': 'Calculating',
  'p2.calc.success': 'Second settlement calculated',
  'p2.calc.error': 'Failed to calculate second settlement',
  'p2.result.title': 'Second settlement result',
  'p2.result.total': 'Second settlement total',
  'p2.result.estimate': 'Second invoice estimate',
  'p2.result.itemsTitle': 'Item breakdown',
  'p2.result.sUnit': 'Second unit price',
  'p2.result.tSubtotal': 'Item subtotal',
  'p2.manual.label': 'EF actual invoice amount (manual)',
  'p2.manual.hint': 'Manually enter the EF actual invoice amount (T38) for the latest run.',
  'p2.manual.placeholder': 'Enter amount',
  'p2.manual.submit': 'Save',
  'p2.manual.saving': 'Saving',
  'p2.manual.saved': 'Saved',
  'p2.manual.success': 'EF actual invoice amount saved',
  'p2.issue.cardTitle': 'Issue → payment confirmation',
  'p2.issue.currentState': 'Current state',
  'p2.issue.label': 'Issue second settlement',
  'p2.issue.button': 'Issue',
  'p2.issue.running': 'Issuing…',
  'p2.issue.hint': 'After issuing, proceed to payment confirmation.',
  'p2.issue.needT38': 'Calculate and enter T38 (EF actual invoice amount) before issuing.',
  'p2.issue.notReady': 'Not ready to issue yet (calculation and T38 required).',
  'p2.issue.success': 'Second settlement issued; case advanced to P2 Issued.',
  'p2.issue.error': 'Failed to issue',
  'p2.payment.label': 'Confirm payment',
  'p2.payment.button': 'Confirm payment',
  'p2.payment.running': 'Confirming…',
  'p2.payment.notReady': 'Issue the settlement before confirming payment.',
  'p2.payment.success': 'Payment confirmed; case advanced to P2 Payment Confirmed.',
  'p2.payment.error': 'Failed to confirm payment',
  'p2.manual.error': 'Failed to save',
  'p2.history.title': 'Calculation history',
  'p2.history.run': 'Run',
  'p2.latest.title': 'Current latest run',
  'p2.latest.none': 'No P2 run yet.',
  // ---- S7 final weight ----
  'finalWeight.title': 'Final weight',
  'finalWeight.subtitle':
    'Show Asahi outbound weight, awarded-buyer receiving weight and the final adopted weight step by step, and compute variance.',
  'finalWeight.state.title': 'State & receive advance',
  'finalWeight.state.current': 'Current state',
  'finalWeight.state.lockedHint':
    'The case is closed and locked. Final weight is read-only and cannot be advanced.',
  'finalWeight.state.notReady':
    'Not yet at the stage to receive final weight (second-settlement payment confirmation required first).',
  'finalWeight.receive.button': 'Confirm final-weight receipt',
  'finalWeight.receive.running': 'Receiving',
  'finalWeight.receive.hint': 'Once confirmed the case advances to the final-report stage (FinalWeightReceived).',
  'finalWeight.receive.blockedByVariance':
    'A final-weight variance exceeds 20kg. Resolve the on-site weight audit before receiving.',
  'finalWeight.receive.success': 'Final-weight receipt confirmed',
  'finalWeight.receive.error': 'Failed to confirm final-weight receipt',
  'finalWeight.compare.title': 'Step-by-step weight comparison',
  'finalWeight.compare.empty': 'No receiving weight data yet.',
  'finalWeight.compare.unavailable': 'Outbound/receiving weight data is not available.',
  'finalWeight.col.memberWeight': 'Member declared weight',
  'finalWeight.col.venueWeight': 'Asahi outbound / venue weight',
  'finalWeight.col.finalWeight': 'Final adopted weight',
  'finalWeight.col.variance': 'Variance (kg)',
  'finalWeight.col.status': 'Status',
  'finalWeight.status.ok': 'Venue weight adopted',
  'finalWeight.status.overThreshold': 'Variance > 20kg',
  'finalWeight.overThresholdHint':
    'Final vs prior weight variance is about {kg}kg, exceeding 20kg. An on-site report (Chen/Kudo) is required; the frontend never auto-adjudicates the authoritative weight.',
  'finalWeight.snapshot.none':
    'No standalone final-weight snapshot (venue receiving weight is used as the adopted value).',
  'finalWeight.reviews.title': 'Related weight-variance reviews',
  'finalWeight.reviews.empty': 'No weight-variance reviews yet.',
  // ---- S7 third settlement P3 (disabled by design) ----
  'p3.title': 'Third settlement',
  'p3.subtitle': 'This feature is disabled by design, pending the owner sign-off of the W41/W40 formula.',
  'p3.blocked.title': 'Third settlement is not yet open',
  'p3.blocked.body':
    'The third-settlement (W41/W40) formula is pending owner sign-off, so this page is disabled per the spec. The system performs no third-settlement calculation.',
  'p3.blocked.q2': 'Related open BLOCKER: Q2 (third-settlement formula, engineering spec §6.5).',
  'p3.formula.title': 'Formula placeholders (read-only)',
  'p3.formula.hint': 'The following are pending formula fields, shown for reference only; no real coefficients are filled in.',
  'p3.formula.cell': 'Field',
  'p3.formula.purpose': 'Purpose',
  'p3.formula.value': 'Value',
  'p3.formula.pending': 'Pending owner sign-off',
  'p3.formula.w41': 'W41 third-settlement final amount',
  'p3.formula.w41Desc': 'Third-settlement final total (formula pending).',
  'p3.formula.w40': 'W40 third-settlement variance adjustment',
  'p3.formula.w40Desc': 'Adjustment to the settlement amount from final-weight variance (formula pending).',
  'p3.calc.button': 'Calculate',
  'p3.calc.disabledHint': 'Calculation is disabled: it opens after the owner signs off the third-settlement formula.',
  'p3.probe.title': 'Verify backend guard',
  'p3.probe.hint':
    'Call the third-settlement API to prove the backend blocks an unconfirmed formula (a blocked response is expected; no calculation result is shown).',
  'p3.probe.button': 'Verify API returns blocked',
  'p3.probe.running': 'Verifying',
  'p3.probe.done': 'The backend responded, confirming third settlement is still blocked.',
  'p3.probe.confirmedBlocked': 'Confirmed: the backend blocks third settlement (formula not confirmed).',
  'p3.probe.blockedGeneric': 'The backend returned a blocked response (unexpected error code)',
  'p3.probe.unexpected': 'Anomaly: third settlement returned success. Stop and verify immediately (should not happen).',
  // ---- S8 final report + close ----
  'finalReport.title': 'Final report / close',
  'finalReport.subtitle':
    'Show the report snapshot and three-party confirmations, then close into Closed_Locked.',
  'finalReport.locked.badge': 'Locked',
  'finalReport.locked.title': 'The case is closed and locked',
  'finalReport.locked.body':
    'The case has entered Closed_Locked; business data is 100% immutable and this page is read-only.',
  'finalReport.snapshot.title': 'Report snapshot',
  'finalReport.snapshot.subject': 'Case title',
  'finalReport.snapshot.state': 'Current state',
  'finalReport.snapshot.none': 'No standalone final-report snapshot (shown from the case master and state).',
  'finalReport.snapshot.hash': 'Report hash (blake3)',
  'finalReport.confirm.title': 'Three-party confirmation',
  'finalReport.confirm.hint':
    'Before closing, EF, Yicheng (member) and Asahi (venue) confirm the final report.',
  'finalReport.confirm.confirmed': 'Confirmed',
  'finalReport.confirm.pending': 'Pending',
  'finalReport.confirm.button': 'Confirm',
  'finalReport.confirm.success': 'Confirmation recorded',
  'finalReport.confirm.error': 'Failed to confirm',
  'finalReport.confirm.allConfirmed': 'All three parties have confirmed; ready to close.',
  'finalReport.confirm.progress': 'Three-party confirmation: {n}/{total}.',
  'finalReport.role.ef': 'EF (broker)',
  'finalReport.role.yicheng': 'Yicheng (member)',
  'finalReport.role.asahi': 'Asahi (venue)',
  'finalReport.precondition.title': 'Close precondition',
  'finalReport.precondition.none': 'No weight-variance reviews; the close precondition is satisfied.',
  'finalReport.precondition.allResolved':
    'All weight-variance reviews are resolved; the close precondition is satisfied.',
  'finalReport.precondition.blocked':
    '{n} weight-variance review(s) are unresolved; on-site resolution is required before closing.',
  'finalReport.actions.title': 'Advance actions',
  'finalReport.review.label': 'Open final-report review',
  'finalReport.review.button': 'Open final-report review',
  'finalReport.review.running': 'Processing',
  'finalReport.review.notReady': 'Not yet at the stage to open review (final-weight receipt required first).',
  'finalReport.review.success': 'Final-report review opened',
  'finalReport.review.error': 'Failed to open final-report review',
  'finalReport.close.label': 'Close & lock',
  'finalReport.close.button': 'Close & lock (Closed_Locked)',
  'finalReport.close.running': 'Closing',
  'finalReport.close.hint': 'After closing the case enters Closed_Locked and business data becomes immutable.',
  'finalReport.close.notReady': 'Not yet at the stage to close (open final-report review first).',
  'finalReport.close.blockedByVariance': 'Unresolved weight-variance reviews remain; cannot close.',
  'finalReport.close.blockedByConfirm': 'Not all three parties have confirmed; cannot close.',
  'finalReport.close.success': 'Case closed and locked',
  'finalReport.close.error': 'Failed to close',

  // ---- 2FA / TOTP ----
  'nav.security': 'Account security',
  'desc.security': 'Manage two-factor authentication (2FA / TOTP); high-risk actions require step-up.',
  'twofa.title': 'Two-factor authentication (2FA)',
  'twofa.subtitle':
    'Once enabled, high-risk actions (settlement manual amount, case close, award confirm, weight resolution) require a one-time code.',
  'twofa.status': 'Current status',
  'twofa.enabled': 'Enabled',
  'twofa.disabled': 'Disabled',
  'twofa.codePlaceholder': '6-digit code',
  'twofa.setup.intro':
    'Scan with Google Authenticator / Authy or enter the secret manually to enable 2FA.',
  'twofa.setup.start': 'Start 2FA setup',
  'twofa.setup.starting': 'Generating…',
  'twofa.setup.ready': 'Secret generated. Scan it in your app and enter the code.',
  'twofa.setup.error': 'Failed to generate 2FA secret',
  'twofa.setup.scan': 'Scan QR or enter the secret manually',
  'twofa.setup.scanHint':
    'Add the otpauth URI below to your authenticator app (most apps accept pasting the URI or manual key entry).',
  'twofa.setup.secret': 'Secret',
  'twofa.verify.label': 'Enter the 6-digit code from your app',
  'twofa.verify.submit': 'Verify & enable',
  'twofa.verify.submitting': 'Verifying…',
  'twofa.verify.success': '2FA enabled',
  'twofa.verify.error': 'Code is incorrect or expired',
  'twofa.disable.intro':
    'Disabling 2FA requires a currently valid code. After disabling, high-risk actions no longer require step-up.',
  'twofa.disable.label': 'Enter the 6-digit code to disable',
  'twofa.disable.submit': 'Disable 2FA',
  'twofa.disable.submitting': 'Disabling…',
  'twofa.disable.success': '2FA disabled',
  'twofa.disable.error': 'Failed to disable (wrong code or not enabled)',
  'stepUp.title': 'Two-factor verification required',
  'stepUp.desc':
    'This is a high-risk action. Enter the one-time code from your authenticator app to continue.',
  'stepUp.codeLabel': 'One-time code',
  'stepUp.codePlaceholder': '6-digit code',
  'stepUp.hint': 'If you have not enabled 2FA yet, enable it under "Account security" first.',
  'stepUp.confirm': 'Confirm & submit',
  'iam.error.step_up_required': 'This action requires 2FA to be enabled and a one-time code.',
  'iam.error.step_up_invalid': 'Two-factor code is incorrect or expired.',
  'iam.totp.enabled': '2FA enabled.',
  'iam.totp.disabled': '2FA disabled.',
  // ---- Hourensou (report-contact-consult; spec §10 / §D5) ----
  'hourensou.title': 'Hourensou (Report / Contact / Consult)',
  'hourensou.subtitle':
    'Track and resolve items needing field, numeric, or process confirmation — 20kg weight variance, three-way split P+Q+R≠K, stage overdue, etc.',
  'hourensou.empty': 'No hourensou items yet.',
  'hourensou.loadError': 'Failed to load hourensou list',
  'hourensou.col.type': 'Type',
  'hourensou.col.category': 'Item',
  'hourensou.col.assignee': 'Assignee',
  'hourensou.col.message': 'Message',
  'hourensou.col.status': 'Status',
  'hourensou.col.action': 'Action',
  'hourensou.type.field': 'Field',
  'hourensou.type.numeric': 'Numeric',
  'hourensou.type.process': 'Process',
  'hourensou.status.open': 'Open',
  'hourensou.status.acknowledged': 'Acknowledged',
  'hourensou.status.resolved': 'Resolved',
  'hourensou.resolve.button': 'Mark resolved',
  'hourensou.resolve.notePlaceholder': 'Resolution note (optional)',
  'hourensou.resolve.success': 'Hourensou marked resolved',
  'hourensou.resolve.error': 'Failed to resolve hourensou',

  // ---- Freee SLA + financial documents (spec §10.3 / §B4; no Freee API calls) ----
  'freee.title': 'Freee SLA / Financial documents',
  'freee.subtitle':
    'Invoices / settlement statements are due within 3 business days of settlement (timing & reminders only — no Freee API calls).',
  'freee.sla.heading': 'Freee SLA timers',
  'freee.sla.empty': 'No Freee SLA timers yet.',
  'freee.sla.loadError': 'Failed to load Freee SLA list',
  'freee.sla.col.phase': 'Phase',
  'freee.sla.col.type': 'Document',
  'freee.sla.col.status': 'Status',
  'freee.sla.col.issuedAt': 'Issued at',
  'freee.sla.col.dueAt': 'Due (+3 business days)',
  'freee.sla.col.action': 'Action',
  'freee.sla.type.invoice': 'Invoice',
  'freee.sla.type.receipt': 'Settlement statement',
  'freee.sla.status.pending': 'Pending',
  'freee.sla.status.warning': 'Due soon',
  'freee.sla.status.overdue': 'Overdue',
  'freee.sla.status.completed': 'Completed',
  'freee.sla.complete.button': 'Mark issued',
  'freee.sla.complete.success': 'Freee task marked completed',
  'freee.sla.complete.error': 'Failed to complete Freee task',
  'freee.doc.heading': 'Financial document references',
  'freee.doc.empty': 'No financial document references yet.',
  'freee.doc.loadError': 'Failed to load financial documents',
  'freee.doc.col.type': 'Type',
  'freee.doc.col.freeeNo': 'Freee management no.',
  'freee.doc.col.invoiceNo': 'Invoice no.',
  'freee.doc.col.statementNo': 'Statement no.',
  'freee.doc.col.note': 'Note',
  'freee.doc.form.heading': 'Record a financial document reference',
  'freee.doc.form.docType': 'Type',
  'freee.doc.form.freeeNo': 'Freee management no.',
  'freee.doc.form.invoiceNo': 'Invoice no.',
  'freee.doc.form.statementNo': 'Statement no.',
  'freee.doc.form.attachment': 'Attachment ref',
  'freee.doc.form.note': 'Note',
  'freee.doc.form.submit': 'Record',
  'freee.doc.form.success': 'Financial document reference recorded',
  'freee.doc.form.error': 'Failed to record financial document reference',
  'freee.rbac.forbidden': 'You do not have permission to view / act on this section.',
}

export const dictionaries: Record<Locale, Dictionary> = { 'zh-TW': zhTW, ja, en }

export function normalizeLocale(value: string | null | undefined): Locale {
  if (value === 'ja') return 'ja'
  if (value === 'en') return 'en'
  return 'zh-TW'
}

export function translate(locale: Locale, key: string): string {
  return dictionaries[locale][key] ?? dictionaries['zh-TW'][key] ?? key
}
